// src/pages/DocWriterEditorPage.jsx
// UPDATED 2026-08-11 v6: single-document-owner EditorPage contract.
// The Page now loads/replaces revision HTML intentionally, while DocCanvasEditor
// remains the sole live-document owner between loads. Canvas onChange is only a
// mirror for dirty/local persistence and is no longer fed straight back as the
// Canvas html prop. Save/Preview/Finalize read the live Canvas document directly.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

import DocCanvasEditor from "../components/DocCanvasEditor";       // editor
import DocRevisionHistory from "../components/DocRevisionHistory"; // list + restore / save / finalize
import FileViewerModal from "../components/FileViewerModal";      // in-app document preview
import DocGenerateDraftBar from "../components/DocGenerateDraftBar"; // reused for Apply Revision
import DocDynamicFieldsForm from "../components/DocDynamicFieldsForm";

// ⬇️ Selectors (like Start Page)
import DocToneSelect from "../components/DocToneSelect";
import DocLanguageSelect from "../components/DocLanguageSelect";

import { DEFAULT_REVISION_CAP } from "../utils/docConstants";
import { getAuth } from "firebase/auth";

// Centralized DocWriter API utility — JSON job/status plus binary PDF preview.
import {
  getDocwriterJob as apiGetDocwriterJob,
  getDocwriterUserNotice,
  getRememberedDocwriterJobId,
  loadSession as apiLoadSession,
  exportDoc as apiExportDoc,
  applyRevision as apiApplyRevision,
  listRevisions as apiListRevisions,
  getRevision as apiGetRevision,
  saveContent as apiSaveContent,
  saveRevision as apiSaveRevision,
  previewPdf as apiPreviewPdf,
  publishNow as apiPublishNow,
} from "../utils/docApiClient";

/**
 * DocWriterEditorPage — vertical, mobile-first
 * Order:
 *  1) Editor header
 *  2) Revision History (View / Restore / Edit / Save / Preview PDF / Finalize)
 *  3) EDITOR AREA  (tagged for print/download/finalize)
 *  4) Required revision preferences (tone / reading level / language)
 *  5) Required revision details and length preference
 *  6) Required Terms / Privacy acknowledgement
 *  7) Apply Revision bar
 *
 * 🔖 TAGGING (used by print/download/finalize)
 *   id="doc-print-source"
 *   data-doc-tag="primary"
 *   data-doc-role="editor-content"
 *
 * ✅ SECURITY/GUARDS (enforced, non-negotiable):
 *   G1) Auth required — must be logged in (useAuth()).
 *   G2) sessionId required — must be present in URL/state (truth comes from Start Page).
 *   G3) Start-origin required — must have a Start-origin signal:
 *       - navigation state flag `fromStart === true`, OR
 *       - a sessionStorage token `docw:launch:<sessionId>` set by Start page.
 *     If this gate fails, redirect to Start page. We do NOT consume the token immediately so
 *     that a user can refresh the editor; the token is scoped to the tab/session.
 *
 * 🧭 SOURCES POLICY (file names + URLs):
 *   - The Editor NO LONGER fetches sources from the backend.
 *   - It hydrates from:
 *       1) navigation state: `initialSources` (Start → Editor), then
 *       2) localStorage:     `docw:editor:<sessionId>` (survives refresh),
 *      and then
 *       3) the original Start-page source manifest retained invisibly for revision context.
 *   - The Editor does not display or manage attachments.
 *   - Revisions continue sending the original known source URLs to the backend.
 */

// eslint-disable-next-line no-unused-vars
const BACKEND = process.env.REACT_APP_BACKEND_URL?.replace(/\/+$/, "") || ""; // (kept; not used directly now)
const PRINT_TAG_ID = "doc-print-source";
const TERMS_URL = process.env.REACT_APP_TERMS_URL || "/terms";
const PRIVACY_URL = process.env.REACT_APP_PRIVACY_URL || "/privacy-policy";

const DOCWRITER_GENERIC_USER_ERROR =
  "An error occurred while preparing your document. Please try again later.";

const DOCWRITER_PUBLIC_MAX_PAGES_PER_FILE = 25;
const DOCWRITER_PUBLIC_MAX_TOTAL_PAGES = 50;
const DOCWRITER_PUBLIC_PAGE_LIMIT_MESSAGE =
  `Your supporting files exceed Doc Writer's page limits. Each file may contain up to ${DOCWRITER_PUBLIC_MAX_PAGES_PER_FILE} pages, and the total across all supporting files may not exceed ${DOCWRITER_PUBLIC_MAX_TOTAL_PAGES} pages. Please start a new Doc Writer job after correcting the source file or package.`;

const DOCWRITER_STATUS_UI = {
  queued: {
    key: "queued",
    label: "Queued",
    shortLabel: "Queued",
    text: "Your document request is waiting to start.",
    color: "#64748B",
    percent: 5,
  },
  dispatch_reserved: {
    key: "dispatch_reserved",
    label: "Dispatch reserved",
    shortLabel: "Reserved",
    text: "Your document request is reserved and getting ready for the worker.",
    color: "#2563EB",
    percent: 7,
  },
  worker_received: {
    key: "worker_received",
    label: "Received",
    shortLabel: "Received",
    text: "The Doc Writer worker received your request.",
    color: "#2563EB",
    percent: 9,
  },
  verifying_sources: {
    key: "verifying_sources",
    label: "Verifying sources",
    shortLabel: "Verifying",
    text: "Your supporting files and source package are being checked.",
    color: "#0EA5E9",
    percent: 16,
  },
  extracting_sources: {
    key: "extracting_sources",
    label: "Reading sources",
    shortLabel: "Reading",
    text: "Your source files are being opened and prepared.",
    color: "#0284C7",
    percent: 28,
  },
  summarizing_chunks: {
    key: "summarizing_chunks",
    label: "Reading details",
    shortLabel: "Details",
    text: "Important details are being organized from the source material.",
    color: "#7C3AED",
    percent: 42,
  },
  summarizing_files: {
    key: "summarizing_files",
    label: "Summarizing files",
    shortLabel: "Summarizing",
    text: "Lexa is preparing a reliable summary of each source.",
    color: "#8B5CF6",
    percent: 55,
  },
  building_drafting_packet: {
    key: "building_drafting_packet",
    label: "Preparing draft",
    shortLabel: "Preparing",
    text: "The drafting instructions and supporting context are being assembled.",
    color: "#6366F1",
    percent: 68,
  },
  drafting: {
    key: "drafting",
    label: "Drafting document",
    shortLabel: "Drafting",
    text: "Lexa is writing the document.",
    color: "#F59E0B",
    percent: 82,
  },
  saving: {
    key: "saving",
    label: "Saving document",
    shortLabel: "Saving",
    text: "The completed version is being saved securely.",
    color: "#EA580C",
    percent: 94,
  },
  ready: {
    key: "ready",
    label: "Ready",
    shortLabel: "Ready",
    text: "Your document is ready to edit.",
    color: "#059669",
    percent: 100,
  },
  awaiting_source_correction: {
    key: "awaiting_source_correction",
    label: "Source needs attention",
    shortLabel: "Attention",
    text: "One or more supporting files could not be accepted.",
    color: "#D97706",
    percent: 100,
  },
  abandoned: {
    key: "abandoned",
    label: "Source needs attention",
    shortLabel: "Attention",
    text: "This Doc Writer job was stopped because one or more supporting files could not be accepted.",
    color: "#D97706",
    percent: 100,
  },
  error: {
    key: "error",
    label: "Needs attention",
    shortLabel: "Error",
    text: "The document operation could not be completed.",
    color: "#DC2626",
    percent: 100,
  },
};

const DOCWRITER_COMPACT_STATUS_STEPS = [
  {
    key: "received",
    label: "Received",
    color: "#2563EB",
    statuses: ["queued", "dispatch_reserved", "worker_received", "verifying_sources"],
  },
  {
    key: "reading",
    label: "Reading",
    color: "#0EA5E9",
    statuses: [
      "extracting_sources",
      "summarizing_chunks",
      "summarizing_files",
      "building_drafting_packet",
    ],
  },
  {
    key: "writing",
    label: "Drafting",
    color: "#8B5CF6",
    statuses: ["drafting"],
  },
  {
    key: "saving",
    label: "Saving",
    color: "#F59E0B",
    statuses: ["saving"],
  },
  {
    key: "ready",
    label: "Ready",
    color: "#059669",
    statuses: ["ready"],
  },
];

const DOCWRITER_STATUS_ALIASES = {
  created: "queued",
  intake: "queued",
  uploaded: "queued",
  upload_complete: "queued",
  dispatching: "dispatch_reserved",
  dispatched: "dispatch_reserved",
  dispatch_reserved: "dispatch_reserved",
  reserved: "dispatch_reserved",
  waiting_capacity: "dispatch_reserved",
  capacity_reserved: "dispatch_reserved",
  operation_registered: "dispatch_reserved",
  claim_and_invoke_worker: "dispatch_reserved",
  validating_sources: "verifying_sources",
  verifying_package: "verifying_sources",
  ingesting: "extracting_sources",
  extracting: "extracting_sources",
  chunking: "summarizing_chunks",
  summarizing: "summarizing_files",
  building_packet: "building_drafting_packet",
  preparing_draft: "building_drafting_packet",
  revising: "drafting",
  publishing: "saving",
  complete: "ready",
  completed: "ready",
  locked: "ready",
  finalized: "ready",
  abandoned: "abandoned",
  failed: "error",
  cancelled: "error",
  canceled: "error",
};

function safeObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function firstValue(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return "";
}

function clampPercent(value, fallback = 0) {
  const numeric = Number(value);
  const resolved = Number.isFinite(numeric) ? numeric : Number(fallback) || 0;
  return Math.max(0, Math.min(100, resolved));
}

function normalizeDocwriterStatus(jobLike) {
  const job = safeObject(jobLike);
  const progress = safeObject(job.progress);
  const activeOperation = safeObject(
    job.activeOperation || safeObject(job.operationState).activeOperation
  );
  const operationState = safeObject(job.operationState);
  const abandonment = safeObject(job.abandonment);
  const userNotice = safeObject(job.userNotice);

  if (
    job.abandoned === true ||
    abandonment.state === "abandoned" ||
    userNotice.kind === "source_rejection"
  ) {
    return "abandoned";
  }

  const raw = String(
    firstValue(
      progress.stage,
      progress.status,
      activeOperation.status,
      operationState.activeOperationStatus,
      job.operationStatus,
      job.documentState,
      job.status,
      "queued"
    )
  )
    .trim()
    .toLowerCase();

  return DOCWRITER_STATUS_ALIASES[raw] || raw || "queued";
}

function docwriterStageMeta(jobLike) {
  const normalized = normalizeDocwriterStatus(jobLike);
  return DOCWRITER_STATUS_UI[normalized] || DOCWRITER_STATUS_UI.queued;
}

function docwriterCompactStageIndex(jobLike) {
  const normalized = normalizeDocwriterStatus(jobLike);
  if (
    normalized === "error" ||
    normalized === "awaiting_source_correction" ||
    normalized === "abandoned"
  ) {
    return -1;
  }
  if (normalized === "ready" || normalized === "locked") {
    return DOCWRITER_COMPACT_STATUS_STEPS.length - 1;
  }

  const index = DOCWRITER_COMPACT_STATUS_STEPS.findIndex((step) =>
    step.statuses.includes(normalized)
  );

  return index >= 0 ? index : 0;
}

function currentRevisionIdFromJob(jobLike) {
  const job = safeObject(jobLike);
  const revisionState = safeObject(job.revisionState);
  return String(
    firstValue(
      revisionState.currentRevisionId,
      job.currentRevisionId,
      revisionState.latestRevisionId
    )
  )
    .trim()
    .toUpperCase();
}

function targetRevisionIdFromJob(jobLike) {
  const job = safeObject(jobLike);
  const operationState = safeObject(job.operationState);
  const activeOperation = safeObject(
    job.activeOperation || operationState.activeOperation
  );

  return String(
    firstValue(
      activeOperation.targetRevisionId,
      operationState.activeTargetRevisionId,
      operationState.targetRevisionId,
      job.targetRevisionId
    )
  )
    .trim()
    .toUpperCase();
}

function operationTypeFromJob(jobLike) {
  const job = safeObject(jobLike);
  const operationState = safeObject(job.operationState);
  const activeOperation = safeObject(
    job.activeOperation || operationState.activeOperation
  );

  return String(
    firstValue(
      activeOperation.operationType,
      operationState.activeOperationType,
      operationState.operationType,
      job.operationType
    )
  )
    .trim()
    .toLowerCase();
}

function isRevisionOperation(jobLike) {
  const operationType = operationTypeFromJob(jobLike);
  const targetRevisionId = targetRevisionIdFromJob(jobLike);

  return operationType === "revision" || /^R\d+$/i.test(targetRevisionId);
}

function publicDocwriterUserNotice(noticeLike) {
  const notice = safeObject(noticeLike);
  const message = String(notice.message || "").trim();

  if (!message) return notice;

  const normalizedMessage = message.toLowerCase();
  const isPageLimitNotice =
    normalizedMessage.includes("page") &&
    (
      normalizedMessage.includes("page limit") ||
      normalizedMessage.includes("pages per source package") ||
      normalizedMessage.includes("per-file") ||
      normalizedMessage.includes("per file") ||
      normalizedMessage.includes("supporting files contain") ||
      normalizedMessage.includes("source contains") ||
      normalizedMessage.includes("active package contains") ||
      normalizedMessage.includes("worker limit") ||
      normalizedMessage.includes("supports up to")
    );

  if (!isPageLimitNotice) return notice;

  return {
    ...notice,
    message: DOCWRITER_PUBLIC_PAGE_LIMIT_MESSAGE,
  };
}

function docwriterUserNotice(jobLike) {
  const job = safeObject(jobLike);
  const embedded = safeObject(job.userNotice);

  if (
    embedded.visible === true &&
    String(embedded.message || "").trim()
  ) {
    return publicDocwriterUserNotice(embedded);
  }

  try {
    const normalized = safeObject(getDocwriterUserNotice(job));
    if (
      normalized.visible === true &&
      String(normalized.message || "").trim()
    ) {
      return publicDocwriterUserNotice(normalized);
    }
  } catch (error) {
    console.error(
      "[DocWriterEditorPage] user notice normalization failed:",
      error
    );
  }

  return {
    visible: false,
    terminal: false,
    kind: "",
    action: "",
    title: "",
    message: "",
  };
}

function Spinner({ size = 18, color = "#2563EB" }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: "999px",
        border: `${Math.max(2, Math.round(size / 8))}px solid ${color}33`,
        borderTopColor: color,
        display: "inline-block",
        animation: "docWriterPipelineSpin .75s linear infinite",
        flex: "0 0 auto",
      }}
    />
  );
}

const REVISION_TAB_TONES = [
  {
    activeBg: "#1E40AF",
    softBg: "#DBEAFE",
    border: "#60A5FA",
    text: "#1E3A8A",
  },
  {
    activeBg: "#7C3AED",
    softBg: "#EDE9FE",
    border: "#A78BFA",
    text: "#5B21B6",
  },
  {
    activeBg: "#D97706",
    softBg: "#FEF3C7",
    border: "#FBBF24",
    text: "#92400E",
  },
  {
    activeBg: "#0F766E",
    softBg: "#CCFBF1",
    border: "#2DD4BF",
    text: "#115E59",
  },
  {
    activeBg: "#BE123C",
    softBg: "#FFE4E6",
    border: "#FB7185",
    text: "#9F1239",
  },
  {
    activeBg: "#0369A1",
    softBg: "#E0F2FE",
    border: "#38BDF8",
    text: "#075985",
  },
];

function normalizeRevisionId(value) {
  return String(value || "").trim().toUpperCase();
}

function revisionOrderValue(value) {
  const revisionId = normalizeRevisionId(value);
  if (revisionId === "D1") return 0;

  const match = /^R(\d+)$/.exec(revisionId);
  if (match) return Number(match[1]) + 1;

  return Number.MAX_SAFE_INTEGER;
}

function compareRevisionRecords(a, b) {
  const aId = normalizeRevisionId(a?.id || a?.revisionId);
  const bId = normalizeRevisionId(b?.id || b?.revisionId);
  const orderDifference = revisionOrderValue(aId) - revisionOrderValue(bId);

  if (orderDifference !== 0) return orderDifference;
  return aId.localeCompare(bId);
}

function revisionTabTone(revisionId, index = 0) {
  const normalized = normalizeRevisionId(revisionId);
  if (normalized === "D1") return REVISION_TAB_TONES[0];

  const match = /^R(\d+)$/.exec(normalized);
  const paletteIndex = match
    ? Number(match[1]) % REVISION_TAB_TONES.length
    : Math.max(0, index) % REVISION_TAB_TONES.length;

  return REVISION_TAB_TONES[paletteIndex];
}

function useIsSmallScreen(breakpoint = 1024) {
  const getInitial = () =>
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false;

  const [isSmall, setIsSmall] = useState(getInitial());

  useEffect(() => {
    const onResize = () => setIsSmall(getInitial());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return isSmall;
}

// 🔧 Normalizer: turn plain text / JSON {reply:"..."} into HTML to prevent “lumping”
function normalizeModelOutputToHtml(raw) {
  if (raw == null) return "";
  let s = String(raw);
  // If the backend accidentally returned JSON with {reply: "..."} unwrap it
  const trimmed = s.trim();
  if ((trimmed.startsWith("{") && trimmed.endsWith("}")) || trimmed.startsWith("["))
    try {
      const j = JSON.parse(trimmed);
      if (j && typeof j.reply === "string") s = j.reply;
    } catch {}
  const looksHtml = /<\/?[a-z][\s\S]*>/i.test(s) || s.trim().startsWith("<");
  if (looksHtml) return s; // already HTML
  // Escape + paragraphize
  const esc = s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return esc
    .split(/\n{2,}/)
    .map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`)
    .join("");
}


function plainDocumentTextFromHtml(html) {
  const raw = String(html || "");
  if (!raw.trim() || typeof document === "undefined") return "";

  const holder = document.createElement("div");
  holder.innerHTML = raw;

  return String(holder.textContent || holder.innerText || "")
    .replace(/\u00a0/g, " ")
    .replace(/\r\n/g, "\n")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n[ \t]+/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

const DocWriterEditorPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth() || {};
  const isSmall = useIsSmallScreen();

  // ----- Resolve the stable DocWriter job/session identity -----
  const searchParams = new URLSearchParams(location.search);
  const sessionIdFromQuery = searchParams.get("sessionId");
  const sessionIdFromState = location?.state?.sessionId;
  const sessionId = sessionIdFromQuery || sessionIdFromState || "";

  const jobIdFromQuery = searchParams.get("jobId");
  const jobIdFromState = location?.state?.jobId;
  const rememberedJobId = sessionId ? getRememberedDocwriterJobId(sessionId) : "";
  const jobId = jobIdFromQuery || jobIdFromState || rememberedJobId || "";

  // ----- Start-origin guard (must come from Start page) -----
  const fromStartFlag = location?.state?.fromStart === true;
  const launchKey = sessionId ? `docw:launch:${sessionId}` : null;
  const hasLaunchToken = !!launchKey && sessionStorage.getItem(launchKey) === "1";
  const hasStartPermit = !!fromStartFlag || !!hasLaunchToken;

  // ---------- Core session state ----------
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false); // kept for future explicit saves if needed (no autosave)
  const [toasts, setToasts] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState("");

  // Two-state workspace:
  //   processing -> only the status/progress interface is visible
  //   ready      -> the existing editor workspace is visible
  const [pipelineVisible, setPipelineVisible] = useState(true);
  const [pipelineMode, setPipelineMode] = useState("draft");
  const [pipelineJob, setPipelineJob] = useState(null);
  const [pipelineLoading, setPipelineLoading] = useState(true);
  const [pipelineError, setPipelineError] = useState("");
  const [pipelineMessageOverride, setPipelineMessageOverride] = useState("");
  const [showProcessingDetails, setShowProcessingDetails] = useState(false);
  const [pipelinePollNonce, setPipelinePollNonce] = useState(0);

  const pipelineTimerRef = useRef(null);
  const revisionBaselineRef = useRef("");
  const expectedRevisionRef = useRef("");

  const [session, setSession] = useState({
    sessionId: sessionId || "",
    title: "Untitled Document",
    subjectName: "",
    subjectId: "",
    status: "drafting", // drafting | locked
    revisionCap: DEFAULT_REVISION_CAP ?? 5,
    revisionCount: 0,
    contentHtml: "", // keep a mirror of backend's current content
  });

  // UI history: items from backend revisions (D1 + R*)
  // Shape we use for DocRevisionHistory: {id,label,summary,type,createdAt,wordCount,html?}
  const [revisions, setRevisions] = useState([]);
  const [uiRevisionsUsed, setUiRevisionsUsed] = useState(0); // count of non-draft revisions
  const [selectedRevisionId, setSelectedRevisionId] = useState(null);
  const [revisionSwitchingId, setRevisionSwitchingId] = useState("");
  const revisionLoadRequestRef = useRef(0);
  const selectedRevisionIdRef = useRef("");

  // Editor
  const [editorOpen, setEditorOpen] = useState(true); // toggle UI removed; keep always true
  const editorRef = useRef(null);

  // `canvasSourceHtml` is a LOAD source only. It changes when this Page
  // intentionally opens/restores/switches a saved revision. It is deliberately
  // NOT updated by Canvas onChange.
  //
  // `localHtml` is only a mirror of the live Canvas for dirty state,
  // localStorage survival, and surrounding page UI.
  const [canvasSourceHtml, setCanvasSourceHtml] = useState("");
  const [localHtml, setLocalHtml] = useState("");
  const [focusNonce, setFocusNonce] = useState(0); // signal prop to request focus in DocCanvasEditor

  const replaceCanvasDocument = useCallback((nextHtml) => {
    const html = String(nextHtml || "");

    // State seed covers first mount / remount.
    setCanvasSourceHtml(html);
    setLocalHtml(html);

    // Imperative load covers an already-mounted Canvas even when the requested
    // saved HTML is byte-for-byte equal to the previous source prop. This is
    // important for Restore after unsaved edits.
    try {
      editorRef.current?.setHtml?.(html);
    } catch (error) {
      console.warn(
        "[DocWriterEditorPage] Canvas document replacement failed:",
        error
      );
    }

    return html;
  }, []);

  // Workspace disclosure controls. These hide surrounding page chrome only; the
  // revision/version controls, DocCanvasEditor mount, and Apply Revision bar remain
  // mounted so collapsing page sections can never reset the live Canvas.
  const [introCollapsed, setIntroCollapsed] = useState(false);
  const [revisionRequestCollapsed, setRevisionRequestCollapsed] = useState(false);

  // Autosave-related local state kept for baseline diffs (no actual autosave anymore)
  const [hydrated, setHydrated] = useState(false); // true after initial backend load
  const [dirty, setDirty] = useState(false);       // true when html changed by user
  const [lastSavedHtml, setLastSavedHtml] = useState("");  // last confirmed-saved body
  const [lastSavedTitle, setLastSavedTitle] = useState("Untitled Document"); // last saved title
  // eslint-disable-next-line no-unused-vars
  const [lastSavedAt, setLastSavedAt] = useState(null);

  // ⬇️ REVISION VIEW MODE (for "View" — non-saving and read-only)
  const [previewingId, setPreviewingId] = useState(null);
  const prePreviewHtmlRef = useRef(""); // restores unsaved draft if revision view is exited

  // Keep the Canvas onChange bridge referentially stable. The parent page changes
  // frequently (collapse controls, toasts, revision preferences, polling state), but
  // those changes must not make the Editor behave like it was remounted or re-bound.
  const editorChangeStateRef = useRef({
    hydrated: false,
    previewingId: null,
    lastSavedHtml: "",
  });

  useEffect(() => {
    editorChangeStateRef.current = {
      hydrated,
      previewingId,
      lastSavedHtml,
    };
  }, [hydrated, previewingId, lastSavedHtml]);

  // Exact PDF preview is separate from revision View mode. It does not save,
  // finalize, lock, create a revision, or mutate the Doc Writer job.
  const [pdfPreviewingId, setPdfPreviewingId] = useState("");
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfViewerRecord, setPdfViewerRecord] = useState(null);

  const closePdfPreviewViewer = () => {
    setPdfViewerOpen(false);
    setPdfViewerRecord(null);
  };

  // Keep the version tabs in D1, R1, R2... order, while showing only the selected version below.
  const revisionTabs = useMemo(
    () => revisions.slice().sort(compareRevisionRecords),
    [revisions]
  );

  const visibleRevisions = useMemo(() => {
    if (!revisions.length) return [];
    const selected = revisions.find((revision) => revision.id === selectedRevisionId);
    return [selected || revisions[0]];
  }, [revisions, selectedRevisionId]);

  useEffect(() => {
    if (!revisions.length) {
      selectedRevisionIdRef.current = "";
      setSelectedRevisionId(null);
      return;
    }

    setSelectedRevisionId((current) => {
      const normalizedCurrent = normalizeRevisionId(current);
      const nextId =
        normalizedCurrent &&
        revisions.some(
          (revision) => normalizeRevisionId(revision.id) === normalizedCurrent
        )
          ? normalizedCurrent
          : normalizeRevisionId(
              revisions.slice().sort(compareRevisionRecords)[0]?.id
            );

      selectedRevisionIdRef.current = nextId;
      return nextId || null;
    });
  }, [revisions]);

  useEffect(() => {
    selectedRevisionIdRef.current = normalizeRevisionId(selectedRevisionId);
  }, [selectedRevisionId]);

  // Original Start-page source manifest retained invisibly for backend revision context.
  // Unified source shape:
  //   { id, name, size?, pages?, status, note?, url? (primary), fileUrl?, fileUrls?[], uploading?, progress? }
  const [sources, setSources] = useState([]);

  // 👉 Race guard to prevent “lumping” when two requests finish out-of-order
  const latestReqIdRef = useRef(0);

  const plainTextLen = (html = "") => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html || "";
    const text = tmp.innerText || tmp.textContent || "";
    return text.replace(/\s+/g, " ").trim().length;
  };

  
  // Revision preferences. Tone, reading level, language, details, and length are
  // user-changeable on the Editor Page. Audience is intentionally NOT editable here:
  // it is inherited from the original D1 request and retained only as revision context.
  const [changeCategory, setChangeCategory] = useState("");
  const [changeTone, setChangeTone] = useState("formal");
  const [changeAudience, setChangeAudience] = useState("");
  const [changeReadingLevel, setChangeReadingLevel] = useState("standard");
  const [changeLanguage, setChangeLanguage] = useState("auto");

  const [fieldValues, setFieldValues] = useState({
    details: "",
    lengthPreference: { mode: "auto" },
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [revisionValidationErrors, setRevisionValidationErrors] = useState({});

  // ---------- LocalStorage Survival ----------
  const STORAGE_KEY = useMemo(
    () => (sessionId ? `docw:editor:${sessionId}` : null),
    [sessionId]
  );

  // Derive a stable ID — mirrors Start page logic (kept verbatim where possible)
  const deriveId = (f) => {
    if (f?.id) return String(f.id);
    const name = f?.name || "unknown";
    const size = f?.size ?? 0;
    const mime = f?.mime || f?.type || "";
    const urlTail = (f?.fileUrl || f?.url || "").split("/").pop()?.slice(0, 16) || "";
    return `${name}::${size}::${mime}::${urlTail}`;
  };

  // Try to match a final event (has URL) to an existing optimistic row — mirrors Start page
  const findMergeId = (f, prevList) => {
    const derived = deriveId(f);
    if (prevList.some((x) => x.id === derived)) return derived;
    const incomingHasUrl =
      !!f?.fileUrl ||
      (Array.isArray(f?.fileUrls) && f.fileUrls.length > 0) ||
      !!f?.url;
    if (incomingHasUrl) {
      const byNameProcessing = prevList.find(
        (x) => x.name === f.name && (x.uploading || x.status === "processing")
      );
      if (byNameProcessing) return byNameProcessing.id;
    }
    return derived;
  };

  // Upsert/merge sources list (dedupe, progress merge, URL merge) — mirrors Start page
  const upsertSources = (incoming = []) => {
    if (!Array.isArray(incoming) || incoming.length === 0) return;
    setSources((prev) => {
      const map = new Map(prev.map((x) => [x.id, x]));
      for (const f of incoming) {
        const id = findMergeId(f, prev);
        const prevItem = map.get(id) || {};

        // Normalize incoming URL fields
        const incomingUrls = Array.isArray(f.fileUrls)
          ? f.fileUrls
          : f.fileUrl
          ? [f.fileUrl]
          : f.url
          ? [f.url]
          : [];
        const prevUrls = Array.isArray(prevItem.fileUrls)
          ? prevItem.fileUrls
          : prevItem.fileUrl
          ? [prevItem.fileUrl]
          : prevItem.url
          ? [prevItem.url]
          : [];
        const mergedUrls = Array.from(new Set([...prevUrls, ...incomingUrls].filter(Boolean)));
        const hasFinalUrl = mergedUrls.length > 0;

        // Uploading flags / progress
        const uploadingFlag =
          typeof f.uploading === "boolean" ? f.uploading : (prevItem.uploading ?? false);
        const progressVal =
          typeof f.progress === "number"
            ? Math.max(0, Math.min(100, f.progress))
            : typeof prevItem.progress === "number"
            ? prevItem.progress
            : uploadingFlag
            ? 5
            : 0;

        // Status resolution
        let statusVal;
        if (f.status) {
          statusVal = f.status;
        } else if (hasFinalUrl) {
          statusVal = "ready";
        } else if (uploadingFlag) {
          statusVal = "processing";
        } else if (progressVal >= 100) {
          statusVal = "ready";
        } else {
          statusVal = prevItem.status || "processing";
        }

        // Once ready, finalize upload flags
        const finalUploading = hasFinalUrl ? false : uploadingFlag;
        const finalProgress = hasFinalUrl ? 100 : progressVal;

        // Compute pages if we see multiple URLs (typical image-per-page expansion)
        const pages = Number.isFinite(f.pages)
          ? f.pages
          : Number.isFinite(prevItem.pages)
          ? prevItem.pages
          : mergedUrls.length > 1
          ? mergedUrls.length
          : undefined;

        const normalized = {
          id,
          name: f.name ?? prevItem.name ?? "Untitled",
          size: typeof f.size === "number" ? f.size : prevItem.size,
          pages,
          note: f.note ?? prevItem.note,

          status: statusVal,
          uploading: finalUploading,
          progress: finalProgress,

          fileUrl: hasFinalUrl
            ? f.fileUrl || prevItem.fileUrl || mergedUrls[0]
            : f.fileUrl ?? prevItem.fileUrl ?? null,
          fileUrls: mergedUrls,
          url: hasFinalUrl
            ? f.url || prevItem.url || mergedUrls[0]
            : f.url ?? prevItem.url ?? null,

          previewUrl: f.previewUrl ?? prevItem.previewUrl ?? null,
        };

        map.set(id, { ...prevItem, ...normalized });
      }
      return Array.from(map.values());
    });
  };

  // Load from localStorage early (also hydrate sources) — we do not overwrite backend-loaded content
  useEffect(() => {
    if (!STORAGE_KEY) return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);

      // Saved editor HTML + session shell. This is an intentional document
      // hydration, so seed the Canvas load source as well as the page mirror.
      if (typeof saved?.localHtml === "string") {
        setCanvasSourceHtml(saved.localHtml);
        setLocalHtml(saved.localHtml);
      }
      if (saved?.session) setSession((prev) => ({ ...prev, ...saved.session }));

      // Saved revisions shell list
      if (Array.isArray(saved?.revisions)) setRevisions(saved.revisions);
      if (Number.isFinite(saved?.uiRevisionsUsed)) setUiRevisionsUsed(saved.uiRevisionsUsed);

      // Saved selector state + form fields
      if (typeof saved?.changeCategory === "string") setChangeCategory(saved.changeCategory);
      if (typeof saved?.changeTone === "string") {
        const savedTone = saved.changeTone.trim().toLowerCase();
        setChangeTone(savedTone && savedTone !== "auto" ? savedTone : "formal");
      }
      if (typeof saved?.changeAudience === "string") setChangeAudience(saved.changeAudience);
      if (typeof saved?.changeReadingLevel === "string") setChangeReadingLevel(saved.changeReadingLevel);
      if (typeof saved?.changeLanguage === "string") setChangeLanguage(saved.changeLanguage);

      if (saved?.fieldValues && typeof saved.fieldValues === "object") {
        setFieldValues((previous) => ({
          ...previous,
          ...saved.fieldValues,
          lengthPreference:
            saved.fieldValues.lengthPreference ||
            previous.lengthPreference ||
            { mode: "auto" },
        }));
      }

      // Saved sources (persisted from prior session / refresh)
      if (Array.isArray(saved?.sources) && saved.sources.length) {
        upsertSources(saved.sources);
      }
    } catch (e) {
      console.warn("[DocWriterEditorPage] localStorage read failed:", e);
    }
    // eslint-disable-next-line
  }, [STORAGE_KEY]);

  // Save to localStorage (skip during PREVIEW to avoid persisting viewed content)
  useEffect(() => {
    if (!STORAGE_KEY) return;
    if (previewingId) return; // do not persist preview content
    try {
      const payload = {
        localHtml,
        uiRevisionsUsed,
        changeCategory,
        changeTone,
        changeAudience,
        changeReadingLevel,
        changeLanguage,
        fieldValues,
        session: {
          sessionId: session.sessionId,
          title: session.title,
          subjectName: session.subjectName,
          subjectId: session.subjectId,
          status: session.status,
          revisionCap: session.revisionCap,
          revisionCount: session.revisionCount,
          contentHtml: session.contentHtml,
        },
        // keep revision shells only; avoid huge HTML dumps in localStorage
        revisions: revisions.map((r) => ({
          id: r.id,
          label: r.label,
          summary: r.summary,
          type: r.type,
          createdAt: r.createdAt,
          wordCount: r.wordCount,
        })),
        // ⬇️ persist sources so file names + URLs survive refresh
        sources,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      console.warn("[DocWriterEditorPage] localStorage write failed:", e);
    }
  }, [
    STORAGE_KEY,
    localHtml,
    uiRevisionsUsed,
    changeTone,
    changeLanguage,
    fieldValues,
    session.sessionId,
    session.title,
    session.subjectName,
    session.subjectId,
    session.status,
    session.revisionCap,
    session.revisionCount,
    session.contentHtml,
    revisions,
    previewingId,
    sources,
    changeCategory,
    changeAudience,
    changeReadingLevel,
  ]);

  // ----- Styles (inline) -----
  const s = useMemo(
    () => ({
      page: {
        width: "100%",
        maxWidth: 1120,
        minWidth: 0,
        margin: "0 auto",
        padding: "18px 16px 44px",
        background:
          "radial-gradient(ellipse 82% 68% at 58% 28%, rgba(37,99,235,0.08) 0%, rgba(37,99,235,0.04) 46%, rgba(37,99,235,0) 100%), radial-gradient(ellipse 72% 58% at 36% 72%, rgba(59,130,246,0.045) 0%, rgba(59,130,246,0.022) 48%, rgba(59,130,246,0) 100%), #FFFFFF",
        color: "#0F172A",
        fontFamily: "sans-serif",
        minHeight: "100vh",
        overflowX: "hidden",
        boxSizing: "border-box",
      },
      pageMobile: { padding: "14px 8px 34px" },
      pageHeader: { marginBottom: 18, paddingTop: 12 },
      pageHeaderTop: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 14,
        minWidth: 0,
      },
      pageHeaderText: { minWidth: 0 },
      // Match the compact show/hide control used on the Doc Writer Start page.
      collapseButton: {
        appearance: "none",
        width: 38,
        height: 38,
        minWidth: 38,
        flex: "0 0 38px",
        border: "1px solid rgba(2,6,23,0.08)",
        borderRadius: 13,
        background: "#F1F5F9",
        color: "#0F172A",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
        cursor: "pointer",
        fontSize: 21,
        lineHeight: 1,
        fontWeight: 900,
        transition:
          "background 130ms ease, border-color 130ms ease, transform 130ms ease",
      },
      collapsibleContent: (collapsed) => ({
        display: collapsed ? "none" : "block",
      }),
      eyebrow: {
        color: "#2563EB",
        fontWeight: 900,
        letterSpacing: ".08em",
        textTransform: "uppercase",
        fontSize: 13,
        marginBottom: 7,
      },
      eyebrowMobile: { textAlign: "center" },
      h1: {
        margin: 0,
        fontSize: 30,
        lineHeight: 1.12,
        fontWeight: 950,
        letterSpacing: "-0.035em",
      },
      h1Mobile: { fontSize: 28, lineHeight: 1.18, textAlign: "center" },
      subtitle: {
        margin: "8px 0 0",
        color: "#475569",
        fontSize: 15,
        lineHeight: 1.55,
        fontWeight: 650,
        maxWidth: 840,
      },
      subtitleMobile: {
        textAlign: "center",
        marginLeft: "auto",
        marginRight: "auto",
      },
      pageGrid: {
        display: "grid",
        gridTemplateColumns: "minmax(0,1fr)",
        alignItems: "start",
      },
      pageGridMobile: { gridTemplateColumns: "1fr" },
      mainColumn: { minWidth: 0 },
      pane: {
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        minHeight: 0,
        overflow: "visible",
        position: "relative",
        background: "#FFFFFF",
        border: "1px solid rgba(2,6,23,0.10)",
        borderRadius: 18,
        boxShadow: "0 14px 34px rgba(2,6,23,0.07)",
      },
      paneHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        padding: "13px 16px",
        background: "#F8FAFC",
        borderBottom: "1px solid rgba(2,6,23,0.08)",
        flexWrap: "wrap",
      },
      paneBody: (open) => ({
        display: open ? "flex" : "none",
        flexDirection: "column",
        minHeight: open ? 240 : 0,
      }),
      paneScroll: {
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        padding: 18,
        scrollbarGutter: "stable both-edges",
      },
      // DocCanvasEditor owns the document viewport and its scrollbar. This page-level
      // shell is intentionally neutral: it does not constrain height, create another
      // vertical scroller, hide overflow, or intercept wheel/keyboard interaction.
      canvasMountShell: {
        display: "block",
        width: "100%",
        minWidth: 0,
        minHeight: "auto",
        height: "auto",
        maxHeight: "none",
        overflow: "visible",
        padding: 18,
        position: "relative",
        overflowAnchor: "none",
      },
      headerNote: { fontSize: 12, color: "#64748B", fontWeight: 700 },
      editorPaneWithTabs: {
        marginTop: 54,
      },
      revisionTabsDock: {
        position: "absolute",
        top: -47,
        right: 16,
        zIndex: 8,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "flex-end",
        gap: 7,
        maxWidth: "calc(100% - 32px)",
        padding: "7px 4px 0",
        overflowX: "auto",
        overflowY: "hidden",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      },
      revisionTab: (tone, active, switching) => ({
        "--docwriter-tab-bg": active ? tone.activeBg : tone.softBg,
        "--docwriter-tab-border": tone.border,
        appearance: "none",
        position: "relative",
        isolation: "isolate",
        minWidth: 62,
        height: 43,
        border: `1px solid ${tone.border}`,
        borderBottomColor: active ? "#FFFFFF" : tone.border,
        background: active ? tone.activeBg : tone.softBg,
        color: active ? "#FFFFFF" : tone.text,
        padding: "10px 15px 8px",
        borderRadius: "12px 12px 4px 4px",
        cursor: "pointer",
        fontSize: 13,
        fontWeight: 950,
        lineHeight: 1,
        letterSpacing: ".02em",
        boxShadow: active
          ? `0 9px 20px ${tone.border}55, 0 2px 0 #FFFFFF`
          : "0 5px 12px rgba(15,23,42,0.10)",
        transform: active ? "translateY(1px)" : "translateY(0)",
        transition:
          "transform .13s ease, box-shadow .13s ease, filter .13s ease, background .13s ease",
        filter: switching ? "saturate(.78)" : "none",
        whiteSpace: "nowrap",
        zIndex: active ? 3 : 1,
      }),
      revisionTabInner: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 7,
      },
      revisionTabLoading: {
        width: 12,
        height: 12,
        borderRadius: 999,
        border: "2px solid rgba(255,255,255,0.42)",
        borderTopColor: "#FFFFFF",
        animation: "docWriterPipelineSpin .7s linear infinite",
      },
      ghostButton: {
        background: "transparent",
        color: "#111827",
        padding: "8px 12px",
        borderRadius: 10,
        border: "1px solid #d1d5db",
        cursor: "pointer",
        fontWeight: 500,
      },
      editorTypingFix: {
        direction: "ltr",
        unicodeBidi: "plaintext",
        textAlign: "left",
      },
      sectionPad: { padding: 18 },
      divider: {
        height: 1,
        background: "rgba(2,6,23,0.08)",
        margin: "18px 0",
      },
      requestIntro: {
        display: "grid",
        gridTemplateColumns: "44px minmax(0,1fr) 38px",
        gap: 12,
        alignItems: "center",
        marginBottom: 18,
      },
      requestBadge: {
        width: 34,
        height: 34,
        borderRadius: 999,
        background: "rgba(37,99,235,0.12)",
        border: "1px solid rgba(37,99,235,0.16)",
        color: "#2563EB",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 16,
        fontWeight: 950,
      },
      requestContent: { minWidth: 0 },
      requestTitle: {
        margin: "1px 0 7px",
        fontSize: 20,
        lineHeight: 1.25,
        fontWeight: 950,
        letterSpacing: "-0.3px",
      },

      fieldBlock: { marginBottom: 16 },
      label: {
        display: "block",
        color: "#0F172A",
        fontWeight: 850,
        marginBottom: 8,
      },
      labelRow: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
        marginBottom: 8,
      },
      requiredPill: {
        border: "1px solid #FECACA",
        borderRadius: 999,
        background: "#FEF2F2",
        color: "#B91C1C",
        padding: "4px 7px",
        fontSize: 9.5,
        lineHeight: 1,
        fontWeight: 950,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
      },
      validationBlock: {
        border: "1px solid transparent",
        borderRadius: 14,
        padding: 12,
        margin: "-12px -12px 16px",
        transition: "border-color 140ms ease, background 140ms ease, box-shadow 140ms ease",
      },
      validationBlockError: {
        borderColor: "#DC2626",
        background: "#FEF2F2",
        boxShadow: "0 0 0 3px rgba(220,38,38,0.10)",
      },
      validationMessage: {
        marginTop: 8,
        color: "#B91C1C",
        fontSize: 12.5,
        lineHeight: 1.4,
        fontWeight: 850,
      },
      termsAgreement: (invalid) => ({
        width: "100%",
        marginTop: 14,
        border: invalid
          ? "1px solid #DC2626"
          : "1px solid rgba(37,99,235,0.16)",
        borderRadius: 13,
        background: invalid ? "#FEF2F2" : "#F8FAFF",
        boxShadow: invalid ? "0 0 0 3px rgba(220,38,38,0.10)" : "none",
        padding: "12px 13px",
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        cursor: "pointer",
      }),
      termsCheckbox: {
        width: 18,
        height: 18,
        flex: "0 0 18px",
        margin: "1px 0 0",
        accentColor: "#2563EB",
        cursor: "pointer",
      },
      termsText: {
        color: "#334155",
        fontSize: 13,
        lineHeight: 1.5,
        fontWeight: 750,
      },

      // Two-state processing/status interface
      pipelineShell: {
        width: "100%",
        maxWidth: 1180,
        margin: "0 auto",
      },
      pipelineHero: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 18,
        marginBottom: 18,
      },
      pipelineStatusPill: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 9,
        minWidth: 142,
        padding: "10px 14px",
        borderRadius: 999,
        border: "1px solid rgba(37,99,235,0.18)",
        background: "#FFFFFF",
        fontSize: 13,
        fontWeight: 950,
        whiteSpace: "nowrap",
        boxShadow: "0 8px 22px rgba(15,23,42,0.06)",
      },
      pipelinePanel: {
        borderRadius: 18,
        border: "1px solid rgba(2,6,23,0.10)",
        background: "#FFFFFF",
        boxShadow: "0 14px 34px rgba(2,6,23,0.07)",
        padding: 18,
      },
      pipelineTop: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 14,
      },
      pipelineTitle: {
        margin: 0,
        fontSize: 20,
        lineHeight: 1.25,
        fontWeight: 950,
        letterSpacing: "-0.3px",
      },
      pipelineCopy: {
        margin: "7px 0 0",
        color: "#475569",
        fontSize: 13.5,
        lineHeight: 1.55,
        fontWeight: 700,
      },
      pipelineJobBadge: {
        maxWidth: 360,
        padding: "7px 10px",
        borderRadius: 10,
        background: "#F8FAFC",
        border: "1px solid rgba(15,23,42,0.09)",
        color: "#475569",
        fontSize: 11,
        fontWeight: 800,
        overflowWrap: "anywhere",
        wordBreak: "break-word",
      },
      processingNote: {
        marginTop: 15,
        padding: "12px 13px",
        borderRadius: 13,
        background: "#EFF6FF",
        border: "1px solid rgba(37,99,235,0.14)",
      },
      processingNoteHead: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
      },
      processingNoteLead: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        minWidth: 0,
      },
      processingNoteIcon: {
        width: 22,
        height: 22,
        minWidth: 22,
        borderRadius: 999,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#2563EB",
        color: "#FFFFFF",
        fontSize: 12,
        fontWeight: 950,
      },
      processingNoteTitle: {
        color: "#1E3A8A",
        fontSize: 12.75,
        lineHeight: 1.4,
      },
      processingNoteToggle: {
        appearance: "none",
        border: 0,
        background: "transparent",
        color: "#1D4ED8",
        padding: 0,
        cursor: "pointer",
        fontSize: 11.75,
        fontWeight: 900,
        whiteSpace: "nowrap",
      },
      processingNoteText: {
        margin: "9px 0 0",
        color: "#1E40AF",
        fontSize: 12.25,
        lineHeight: 1.55,
        fontWeight: 700,
      },
      instrumentPanel: {
        marginTop: 16,
        borderRadius: 14,
        padding: 14,
        background: "#F8FAFC",
        border: "1px solid rgba(15,23,42,0.08)",
      },
      instrumentTop: {
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-between",
        gap: 12,
        marginBottom: 10,
      },
      instrumentLabel: {
        color: "#0F172A",
        fontSize: 12.5,
        fontWeight: 900,
      },
      instrumentSub: {
        marginTop: 3,
        color: "#64748B",
        fontSize: 11.5,
        fontWeight: 750,
      },
      instrumentPercent: {
        fontSize: 24,
        lineHeight: 1,
        fontWeight: 950,
      },
      segmentTrack: {
        width: "100%",
        height: 10,
        overflow: "hidden",
        borderRadius: 999,
        background: "#E2E8F0",
      },
      segmentFill: {
        height: "100%",
        borderRadius: 999,
        transition: "width .35s ease",
      },
      segmentLabels: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
        marginTop: 8,
        color: "#64748B",
        fontSize: 10.5,
        fontWeight: 800,
      },
      stageGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
        gap: 10,
        marginTop: 14,
        marginBottom: 2,
      },
      stageCard: {
        border: "1px solid rgba(15,23,42,0.08)",
        borderRadius: 16,
        background: "#FFFFFF",
        padding: "11px 12px",
        minHeight: 88,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        transition:
          "border-color .2s ease, background .2s ease, box-shadow .2s ease, opacity .2s ease",
      },
      stageDone: {
        background: "#F8FAFC",
      },
      stageCurrent: {
        background: "#FFFFFF",
      },
      stagePending: {
        background: "#FFFFFF",
        opacity: 0.78,
      },
      stageTop: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
        marginBottom: 8,
      },
      stageIcon: {
        width: 28,
        height: 28,
        minWidth: 28,
        borderRadius: 999,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 11,
        fontWeight: 950,
        flex: "0 0 auto",
      },
      stageLabel: {
        color: "#0F172A",
        fontSize: 12,
        fontWeight: 950,
        lineHeight: 1.25,
        overflowWrap: "anywhere",
      },
      pipelineNotice: {
        marginTop: 14,
        padding: "12px 13px",
        borderRadius: 12,
        fontSize: 12.5,
        lineHeight: 1.5,
        fontWeight: 750,
      },
      pipelineActions: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 9,
        marginTop: 12,
      },
      pipelineButton: {
        appearance: "none",
        borderRadius: 10,
        padding: "9px 12px",
        border: "1px solid rgba(37,99,235,0.20)",
        background: "#2563EB",
        color: "#FFFFFF",
        cursor: "pointer",
        fontSize: 12,
        fontWeight: 900,
      },
      pipelineSecondaryButton: {
        appearance: "none",
        borderRadius: 10,
        padding: "9px 12px",
        border: "1px solid rgba(15,23,42,0.14)",
        background: "#FFFFFF",
        color: "#0F172A",
        cursor: "pointer",
        fontSize: 12,
        fontWeight: 900,
      },

      // In-app PDF preview backdrop. FileViewerModal remains above this layer.
      pdfPreviewBackdrop: {
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(15,23,42,0.22)",
        backdropFilter: "blur(1px)",
        WebkitBackdropFilter: "blur(1px)",
        pointerEvents: "none",
      },

      // Toasts & Loading
      toastWrap: {
        position: "fixed",
        right: 16,
        bottom: 16,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        zIndex: 45,
      },
      toast: (type) => ({
        background:
          type === "error"
            ? "#fee2e2"
            : type === "success"
            ? "#dcfce7"
            : "#f3f4f6",
        color: "#111827",
        border: "1px solid #e5e7eb",
        borderLeft: `4px solid ${
          type === "error" ? "#dc2626" : type === "success" ? "#16a34a" : "#6b7280"
        }`,
        padding: "10px 12px",
        borderRadius: 10,
        minWidth: 280,
        boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
      }),
      loadingOverlay: {
        position: "fixed",
        inset: 0,
        background: "rgba(255,255,255,0.6)",
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 600,
      },

      // Preview tag
      previewTag: {
        fontSize: 12,
        color: "#b45309",
        background: "#fffbeb",
        border: "1px solid #fcd34d",
        padding: "4px 8px",
        borderRadius: 8,
        marginLeft: 8,
        whiteSpace: "nowrap",
      },

      // Subject lock UI
      lockWrap: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
      },
      lockPill: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 10px",
        borderRadius: 999,
        border: "1px solid #e5e7eb",
        background: "#f9fafb",
        fontSize: 13,
        fontWeight: 600,
        color: "#111827",
      },
      lockNote: { fontSize: 12, color: "#6b7280" },

      // Category badge
      catBadge: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 8px",
        borderRadius: 999,
        border: "1px solid #e5e7eb",
        background: "#f3f4f6",
        fontSize: 12,
        color: "#374151",
        marginTop: 6,
      },
    }),
    []
  );

  // ---------- Helpers ----------
  const addToast = (message, type = "info") => {
    const id = Math.random().toString(36).slice(2);
    setToasts((t) => [...t, { id, message, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3500);
  };

  const clearRevisionValidationError = (key) => {
    setRevisionValidationErrors((previous) => {
      if (!previous[key]) return previous;
      const next = { ...previous };
      delete next[key];
      return next;
    });
  };

  const focusRevisionValidationTarget = (targetId, preferredSelectors = []) => {
    // Apply Revision remains visible even when the request form is collapsed. If a
    // required field needs attention, reveal the form before scrolling/focusing it.
    setRevisionRequestCollapsed(false);
    window.setTimeout(() => {
      const target = document.getElementById(targetId);
      if (!target) return;

      target.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      const selectors = [
        ...preferredSelectors,
        '[aria-invalid="true"]',
        'input:not([type="hidden"])',
        "select",
        "textarea",
        "button",
      ];

      const focusable = selectors
        .map((selector) => target.querySelector(selector))
        .find((element) => element && !element.disabled);

      window.setTimeout(() => {
        focusable?.focus?.({ preventScroll: true });
      }, 320);
    }, 60);
  };
  // helper: count visible characters from HTML
  const getPlainTextLength = (html = "") => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html || "";
    const text = tmp.innerText || tmp.textContent || "";
    return text.trim().length;
  };

  const getIdToken = async () => {
    const auth = getAuth();
    const token =
      (currentUser && (await currentUser.getIdToken?.(true))) ||
      (await auth.currentUser?.getIdToken?.(true));
    return token || "";
  };

  useEffect(() => {
    if (!sessionId || !jobId) {
      addToast("Missing Doc Writer job information. Please start a new document first.", "error");
      navigate("/docwriter/start");
      return;
    }

    // G1: must be logged in
    if (!currentUser) {
      navigate("/login", {
        state: {
          from: `/docwriter/editor?jobId=${encodeURIComponent(
            jobId
          )}&sessionId=${encodeURIComponent(sessionId)}`,
        },
      });
      return;
    }

    // G3: must come from Start page (state flag OR sessionStorage token). Reject direct URL.
    if (!hasStartPermit) {
      addToast("Open the editor from the Start page (direct URL is blocked).", "error");
      navigate("/docwriter/start");
      return;
    }

    // IMPORTANT: keep token in sessionStorage so refresh works during this session.
    // eslint-disable-next-line
  }, [sessionId, jobId, currentUser, hasStartPermit]);

  // Legacy JSON helper retained for compatibility. Diagnostics stay in the
  // console; customer-facing failures use the generic Doc Writer message.
  // eslint-disable-next-line no-unused-vars
  const fetchJson = async (url, options = {}, tag = "fetchJson") => {
    const res = await fetch(url, options);
    const ct = res.headers.get("content-type") || "";
    if (ct.includes("application/json")) {
      const json = await res.json();
      if (!res.ok) {
        console.error(`[DocWriterEditorPage] ${tag} failed`, {
          status: res.status,
          response: json,
        });
        throw new Error(DOCWRITER_GENERIC_USER_ERROR);
      }
      return json;
    }

    const responseText = await res.text();
    try {
      const json = JSON.parse(responseText);
      if (!res.ok) {
        console.error(`[DocWriterEditorPage] ${tag} failed`, {
          status: res.status,
          response: json,
        });
        throw new Error(DOCWRITER_GENERIC_USER_ERROR);
      }
      return json;
    } catch (error) {
      console.error(`[DocWriterEditorPage] ${tag} returned an unreadable response`, {
        status: res.status,
        error,
      });
      throw new Error(DOCWRITER_GENERIC_USER_ERROR);
    }
  };

  // ---------- API wrappers (delegate to centralized util) ----------
  const api = {
    async getJob(id) {
      const idToken = await getIdToken();
      const res = await apiGetDocwriterJob(id, idToken);
      if (!res?.ok) {
        throw new Error(res?.error?.message || "Failed to load Doc Writer status");
      }
      return res.data || {};
    },

    async loadSession(id) {
      const idToken = await getIdToken();
      const res = await apiLoadSession(id, idToken);
      if (!res?.ok) throw new Error(res?.error?.message || "Failed to load session");
      return res.data || {};
    },
    async listRevisions(id, { withContent = false, order = "asc", limit = 100 } = {}) {
      const idToken = await getIdToken();
      const res = await apiListRevisions({ sessionId: id, withContent, order, limit }, idToken);
      if (!res?.ok) throw new Error(res?.error?.message || "Failed to list revisions");
      return res.data || res; // normalize
    },
    async getRevision({ jobId: requestedJobId, sessionId, revisionId }) {
      const idToken = await getIdToken();
      const res = await apiGetRevision(
        {
          jobId: requestedJobId || jobId,
          sessionId,
          revisionId,
        },
        idToken
      );
      if (!res?.ok) throw new Error(res?.error?.message || "Failed to get revision");
      return res.data || res;
    },
    // NOTE: loadSources removed — sources are hydrated from Start page / localStorage
    async applyRevision({
      jobId,
      sessionId,
      text,
      attachment,
      lengthPreference,
      length,
      ...rest
    }) {
      const idToken = await getIdToken();
      const res = await apiApplyRevision(
        {
          jobId,
          sessionId,
          text,
          attachment,
          lengthPreference,
          length,
          ...rest,
        },
        idToken
      );
      if (!res?.ok) throw new Error(res?.error?.message || "Revision failed");
      return res.data || { contentHtml: "" };
    },
    // ✅ Explicit save of current content into a SPECIFIC revision (no autosave)
    async saveExactRevision({
      sessionId,
      revisionId,
      contentHtml,
      contentText = "",
      title,
      makeCurrent = true,
    }) {
      const idToken = await getIdToken();
      const res = await apiSaveRevision(
        {
          sessionId,
          revisionId,
          contentHtml,
          textContent: contentText,
          title,
          makeCurrent,
        },
        idToken
      );
      if (!res?.ok) throw new Error(res?.error?.message || "Save failed");
      return res.data || {};
    },
    async saveContent({ sessionId, contentHtml, title }) {
      const idToken = await getIdToken();
      const res = await apiSaveContent({ sessionId, contentHtml, title }, idToken);
      if (!res?.ok) throw new Error(res?.error?.message || "Save failed");
      return res.data || {};
    },
    async previewPdf({
      jobId,
      sessionId,
      revisionId,
      contentHtml,
      contentText = "",
      title,
      subject,
      metadata,
    }) {
      const idToken = await getIdToken();
      const res = await apiPreviewPdf(
        {
          jobId,
          sessionId,
          revisionId,
          contentHtml,
          textContent: contentText,
          title,
          subject,
          metadata,
        },
        idToken
      );

      if (!res?.ok) {
        throw new Error(
          res?.error?.message ||
            "The exact Doc Writer PDF preview could not be prepared."
        );
      }

      return res.data || {};
    },

    async publishNow({
      jobId,
      sessionId,
      revisionId,
      contentHtml,
      format,
      subject,
    }) {
      const idToken = await getIdToken();
      const res = await apiPublishNow(
        {
          jobId,
          sessionId,
          revisionId,
          contentHtml,
          format,
          subject,
        },
        idToken
      );
      if (!res?.ok) throw new Error(res?.error?.message || "Finalize failed");
      return res.data || {};
    },

    async exportDoc({ sessionId, format }) {
      const idToken = await getIdToken();
      const res = await apiExportDoc({ sessionId, format }, idToken);
      if (!res?.ok) throw new Error(res?.error?.message || "Export failed");
      return res.data?.blob;
    },
  };

  // ---------- Sync helpers ----------
  // Preserve the backend's canonical D1/R* identities. The UI never renumbers
  // revisions by creation time because each tab must request that exact pointer.
  const hydrateRevisions = (itemsAsc) => {
    const mapped = (itemsAsc || [])
      .map((it) => {
        const revisionId = normalizeRevisionId(it.id || it.revisionId);
        if (!revisionId) return null;

        const createdAt = it.createdAt || it.created_at || Date.now();
        const type = revisionId === "D1" ? "draft" : "revision";

        return {
          id: revisionId,
          label: `${revisionId} — ${new Date(createdAt).toLocaleString()}`,
          summary: it.instructionSnippet || "",
          type,
          createdAt,
          wordCount: it.wordCount || 0,
          html: it.contentHtml || undefined,
        };
      })
      .filter(Boolean);

    const uiRevisionsUsed = mapped.filter((item) => /^R\d+$/.test(item.id)).length;

    // History remains newest-first; the folder tabs sort D1, R1, R2, R3...
    mapped.sort((a, b) => compareRevisionRecords(b, a));

    return {
      items: mapped,
      uiRevisionsUsed,
    };
  };

  const refreshRevisionsFromBackend = async (id) => {
    const list = await api.listRevisions(id, { withContent: false, order: "asc", limit: 200 });
    const asc = Array.isArray(list?.items) ? list.items : [];
    const { items, uiRevisionsUsed } = hydrateRevisions(asc);
    setRevisions(items);
    setUiRevisionsUsed(uiRevisionsUsed);
    return items;
  };

  const readHtmlFromLoadedSession = async (data = {}) => {
    const revision = safeObject(data.revision);
    const contentHtmlPointer = safeObject(data.contentHtml);
    const htmlPointer = safeObject(revision.html);

    const inlineCandidates = [
      data.contentHtml,
      data.htmlContent,
      data.html,
      revision.htmlContent,
      revision.contentHtml,
    ];

    for (const candidate of inlineCandidates) {
      if (typeof candidate !== "string") continue;
      const value = candidate.trim();
      if (!value || /^https?:\/\//i.test(value)) continue;
      return normalizeModelOutputToHtml(value);
    }

    const candidateUrl = firstValue(
      contentHtmlPointer.readUrl,
      contentHtmlPointer.url,
      htmlPointer.readUrl,
      htmlPointer.url,
      typeof data.contentHtml === "string" ? data.contentHtml : "",
      typeof revision.html === "string" ? revision.html : "",
      revision.htmlUrl,
      revision.htmlReadUrl,
      revision.signedHtmlUrl,
      revision.contentHtmlUrl,
      revision.contentUrl,
      revision.url
    );

    if (!candidateUrl || !/^https?:\/\//i.test(String(candidateUrl))) {
      return "";
    }

    const response = await fetch(String(candidateUrl), {
      method: "GET",
      credentials: "omit",
    });

    if (!response.ok) {
      throw new Error(`Draft content is not readable yet (${response.status}).`);
    }

    return normalizeModelOutputToHtml(await response.text());
  };

  const hydrateEditorFromBackendData = async (data = {}) => {
    const contentHtml = await readHtmlFromLoadedSession(data);
    if (!contentHtml) {
      throw new Error("Draft content is not readable yet.");
    }

    const backendRevisions = Number.isFinite(data.revisionCount)
      ? data.revisionCount
      : 0;

    setSession((prev) => ({
      ...prev,
      sessionId: data.sessionId || sessionId,
      title: data.title || prev.title || "Untitled Document",
      subjectName: prev.subjectName || data.subjectName || "",
      subjectId: data.subjectId || prev.subjectId || "",
      status: data.status || prev.status || "drafting",
      revisionCap: data.revisionCap ?? prev.revisionCap,
      revisionCount: backendRevisions,
      contentHtml,
    }));

    if (!previewingId) replaceCanvasDocument(contentHtml);

    setLastSavedHtml(contentHtml);
    setLastSavedTitle(data.title || session.title || "Untitled Document");
    setDirty(false);
    setHydrated(true);

    setChangeCategory((prev) => (data?.meta?.category || prev || "").trim());
    const hydratedTone = String(data.tone || changeTone || "formal")
      .trim()
      .toLowerCase();
    setChangeTone(hydratedTone && hydratedTone !== "auto" ? hydratedTone : "formal");
    setChangeAudience((data?.meta?.audience || changeAudience || "").trim());
    setChangeReadingLevel(data.readingLevel || changeReadingLevel || "standard");
    setChangeLanguage(data.language || changeLanguage || "auto");

    setFieldValues((previous) => ({
      ...previous,
      // Revision details belong to the next revision request and must not be
      // prefilled from the original draft request.
      details: previous.details || "",
      lengthPreference:
        data?.meta?.lengthPreference ||
        previous.lengthPreference ||
        { mode: "auto" },
    }));

    const refreshedRevisions = await refreshRevisionsFromBackend(
      data.sessionId || sessionId
    );
    const desiredRevisionId = String(
      data.currentRevisionId ||
      safeObject(data.job).revisionState?.currentRevisionId ||
      ""
    )
      .trim()
      .toUpperCase();

    if (desiredRevisionId && refreshedRevisions.some((item) => item.id === desiredRevisionId)) {
      setSelectedRevisionId(desiredRevisionId);
    } else if (refreshedRevisions.length) {
      setSelectedRevisionId(refreshedRevisions[0].id);
    }

    return {
      contentHtml,
      refreshedRevisions,
      currentRevisionId: desiredRevisionId,
    };
  };


  // ---------- Two-state status polling + editor hydration ----------
  useEffect(() => {
    if (
      !pipelineVisible ||
      !sessionId ||
      !jobId ||
      !currentUser ||
      !hasStartPermit
    ) {
      return undefined;
    }

    let stopped = false;

    const scheduleNext = (delayMs) => {
      if (stopped) return;
      if (pipelineTimerRef.current) clearTimeout(pipelineTimerRef.current);
      pipelineTimerRef.current = setTimeout(poll, delayMs);
    };

    const poll = async () => {
      try {
        const envelope = await api.getJob(jobId);
        if (stopped) return;

        const job = safeObject(envelope.job || envelope);
        setPipelineJob(job);
        setPipelineLoading(false);

        const normalizedStatus = normalizeDocwriterStatus(job);
        const detectedRevisionMode = isRevisionOperation(job);
        if (detectedRevisionMode && pipelineMode !== "revision") {
          setPipelineMode("revision");
        }
        const currentRevisionId = currentRevisionIdFromJob(job);
        const expectedRevisionId =
          expectedRevisionRef.current || targetRevisionIdFromJob(job);
        const baselineRevisionId = revisionBaselineRef.current;

        if (expectedRevisionId) {
          expectedRevisionRef.current = expectedRevisionId;
        }

        const userNotice = docwriterUserNotice(job);

        if (
          normalizedStatus === "abandoned" ||
          userNotice.kind === "source_rejection"
        ) {
          setPipelineError(
            String(userNotice.message || "").trim() ||
              "One or more supporting files could not be accepted. Please return to Doc Writer and start a new job with corrected files."
          );
          setPipelineMessageOverride("");
          return;
        }

        if (normalizedStatus === "error") {
          setPipelineError(
            userNotice.kind === "system_error" &&
              String(userNotice.message || "").trim()
              ? userNotice.message
              : DOCWRITER_GENERIC_USER_ERROR
          );
          setPipelineMessageOverride("");
          return;
        }

        if (normalizedStatus === "awaiting_source_correction") {
          setPipelineError(
            String(userNotice.message || "").trim() ||
              "One or more supporting files could not be accepted. Please return to Doc Writer and start a new job with corrected files."
          );
          setPipelineMessageOverride("");
          return;
        }

        setPipelineError("");

        const operationReady =
          normalizedStatus === "ready" ||
          normalizedStatus === "locked";

        const revisionReady =
          pipelineMode === "revision"
            ? Boolean(
                currentRevisionId &&
                  (
                    (expectedRevisionId &&
                      currentRevisionId === expectedRevisionId) ||
                    (!expectedRevisionId &&
                      currentRevisionId !== baselineRevisionId)
                  )
              )
            : Boolean(currentRevisionId);

        if (operationReady && revisionReady) {
          setPipelineMessageOverride(
            pipelineMode === "revision"
              ? "Revision saved. Opening the updated editor…"
              : "Draft saved. Opening your editor…"
          );

          const loaded = await api.loadSession(jobId);
          if (stopped) return;

          await hydrateEditorFromBackendData(loaded);
          if (stopped) return;

          setPipelineMessageOverride("");
          setPipelineVisible(false);
          setLoading(false);
          return;
        }

        setPipelineMessageOverride("");
        scheduleNext(2500);
      } catch (pollError) {
        if (stopped) return;
        console.error("[DocWriterEditorPage] status polling failed:", pollError);
        setPipelineLoading(false);
        setPipelineError(DOCWRITER_GENERIC_USER_ERROR);
        scheduleNext(3500);
      }
    };

    setPipelineLoading(true);
    poll();

    return () => {
      stopped = true;
      if (pipelineTimerRef.current) {
        clearTimeout(pipelineTimerRef.current);
        pipelineTimerRef.current = null;
      }
    };

    // The hydration helpers intentionally use the latest component state.
    
  }, [
    pipelineVisible,
    pipelineMode,
    pipelinePollNonce,
    sessionId,
    jobId,
    currentUser,
    hasStartPermit,
  ]);

  // ---------- Hydrate sources: (A) from Start page state, (B) then from LS (already done above) ----------
  useEffect(() => {
    if (!sessionId || !hasStartPermit) return;

    // If Start Page provided `initialSources`, upsert them first.
    const maybeInitial = location?.state?.initialSources;
    if (Array.isArray(maybeInitial) && maybeInitial.length) {
      const normalized = maybeInitial.map((it) => ({
        ...it,
        id: it.id || deriveId(it),
        url: it.url || it.fileUrl || (Array.isArray(it.fileUrls) ? it.fileUrls[0] : null),
        status: it.status || "ready",
      }));
      upsertSources(normalized);
    }

    // We no longer fetch from backend here (removed apiLoadSources path).
    // eslint-disable-next-line
  }, [sessionId, hasStartPermit]);

  // ---------- Editor change handler (no autosave; only marks dirty) ----------
  const handleEditorChange = useCallback((html) => {
    const nextHtml = String(html || "");

    // IMPORTANT: this is a one-way mirror only.
    // Never copy this value into `canvasSourceHtml`; doing so would turn the Page
    // back into a controlled editor and echo Canvas's own document straight back
    // through its `html` prop.
    setLocalHtml((current) => (current === nextHtml ? current : nextHtml));

    const state = editorChangeStateRef.current;
    if (!state.hydrated || state.previewingId) return;
    setDirty(nextHtml !== state.lastSavedHtml);
  }, []);

  // Live document bridge.
  //
  // When Canvas is mounted it is the ONLY live-document source. The Page never
  // substitutes a stale `localHtml` mirror for an empty Canvas result.
  const readLiveCanvasDocument = useCallback(
    (requestedMode = "save") => {
      const editor = editorRef.current;
      const mode = String(requestedMode || "save").toLowerCase();

      let prepared = null;
      if (editor) {
        try {
          if (mode === "preview") {
            prepared = editor.prepareForPreview?.();
          } else if (mode === "finalize" || mode === "final") {
            prepared = editor.prepareForFinalize?.();
          } else if (mode === "revision") {
            prepared = editor.prepareForRevision?.();
          } else {
            prepared = editor.prepareForSave?.();
          }
        } catch (error) {
          console.warn(
            "[DocWriterEditorPage] live Canvas preparation failed:",
            error
          );
        }
      }

      let html = String(prepared?.html || "");
      let textValue = String(prepared?.text || "");

      if (editor && !html.trim()) {
        try {
          const fallbackMode =
            mode === "preview"
              ? "preview"
              : mode === "finalize" || mode === "final"
              ? "finalize"
              : mode === "revision"
              ? "revision"
              : "serialization";
          html = String(editor.getHtml?.({ mode: fallbackMode }) || "");
        } catch (_) {}
      }

      // Only an actually unmounted/unavailable Canvas may use the parent mirror.
      // A mounted Canvas returning empty means the live document is empty and
      // must not be masked by stale parent state.
      if (!editor && !html.trim()) {
        html = String(localHtml || "");
      }

      if (!textValue.trim() && html.trim()) {
        textValue = plainDocumentTextFromHtml(html);
      }

      return { html, text: textValue };
    },
    [localHtml]
  );

  const canvasReadOnly = session.status === "locked" || !!previewingId;

  // Canvas is intentionally uncontrolled between explicit document loads.
  // `canvasSourceHtml` changes only for backend/revision hydration; Canvas
  // onChange updates `localHtml` but does not feed that value back as a prop.
  const canvasEditorNode = useMemo(
    () => (
      <DocCanvasEditor
        ref={editorRef}
        html={canvasSourceHtml}
        onChange={handleEditorChange}
        readOnly={canvasReadOnly}
        redline={false}
        placeholder="Start typing or paste your content…"
        focusNonce={focusNonce}
      />
    ),
    [
      canvasReadOnly,
      canvasSourceHtml,
      focusNonce,
      handleEditorChange,
    ]
  );

  // ---------- Revision instruction builder (preserves backend field order) ----------
  const buildRevisionInstruction = ({
    subject = "",
    category = "",
    tone = "formal",
    audience = "",
    readingLevel = "standard",
    language = "auto",
    fields = {},
    fileUrls = [],
  }) => {
    const safeCategory =
      category && category.trim() ? category.trim() : "Other / Not Listed";
    const lengthPreference = fields?.lengthPreference || { mode: "auto" };
    const details = String(fields?.details || "").trim();

    return [
      `CATEGORY: ${safeCategory}`,
      `SUBJECT: ${String(subject || "").trim()}`,
      `TONE: ${tone}`,
      `AUDIENCE: ${audience}`,
      `READING_LEVEL: ${readingLevel}`,
      `LANGUAGE: ${language}`,
      "RECIPIENT: {}",
      "SENDER: {}",
      "DUE_DATE: ",
      "CASE_NUMBER: ",
      `LENGTH_PREFERENCE: ${JSON.stringify(lengthPreference)}`,
      "",
      "DETAILS:",
      details,
      "",
      `ATTACHMENT_URLS: ${JSON.stringify(fileUrls)}`,
      "",
      "MODE: revision — update the selected revision according to the requested details.",
      "",
      "INSTRUCTION: Apply the requested changes while preserving existing structure, facts, and the document's inherited audience unless the selected tone, reading level, language, length, or requested details require an appropriate change.",
    ].join("\n");
  };

  // ---------- Apply Revision (validated, no browser pop-up) ----------
  const handleApplyRevision = async () => {
    if (session.status === "locked") {
      addToast("This document is finalized and read-only.", "error");
      return;
    }
    if (uiRevisionsUsed >= (session.revisionCap ?? 5)) {
      addToast("Revision limit reached. Get +5 or duplicate as new doc.", "error");
      return;
    }

    const details = String(fieldValues?.details || "").trim();
    const rawLengthPreference = fieldValues?.lengthPreference;
    const lengthMode = String(
      typeof rawLengthPreference === "string"
        ? rawLengthPreference
        : rawLengthPreference?.mode || ""
    ).trim();

    const checks = [
      {
        key: "tone",
        valid:
          Boolean(String(changeTone || "").trim()) &&
          String(changeTone || "").trim().toLowerCase() !== "auto",
        message: "Select a tone before applying the revision.",
        targetId: "doc-writer-editor-required-preferences",
        selectors: ['[name*="tone"]', '[id*="tone"]', "select"],
      },
      {
        key: "readingLevel",
        valid: Boolean(String(changeReadingLevel || "").trim()),
        message: "Select a reading level before applying the revision.",
        targetId: "doc-writer-editor-required-preferences",
        selectors: ['[name*="reading"]', '[id*="reading"]', "select"],
      },
      {
        key: "language",
        valid: Boolean(String(changeLanguage || "").trim()),
        message: "Select a language before applying the revision.",
        targetId: "doc-writer-editor-required-language",
        selectors: ["select"],
      },
      {
        key: "details",
        valid: Boolean(details),
        message: "Describe the changes you want before applying the revision.",
        targetId: "doc-writer-editor-required-details",
        selectors: ["textarea"],
      },
      {
        key: "lengthPreference",
        valid: Boolean(lengthMode),
        message: "Select a length preference before applying the revision.",
        targetId: "doc-writer-editor-required-details",
        selectors: ["select", "button"],
      },
      {
        key: "terms",
        valid: termsAccepted,
        message: "Accept the Terms of Use and Privacy Policy before applying the revision.",
        targetId: "doc-writer-editor-required-terms",
        selectors: ['input[type="checkbox"]'],
      },
    ];

    const errors = checks.reduce((result, check) => {
      if (!check.valid) result[check.key] = check.message;
      return result;
    }, {});
    const firstInvalid = checks.find((check) => !check.valid);

    if (firstInvalid) {
      setRevisionValidationErrors(errors);
      addToast(firstInvalid.message, "error");
      focusRevisionValidationTarget(
        firstInvalid.targetId,
        firstInvalid.selectors
      );
      return;
    }

    setRevisionValidationErrors({});

    // Race guard: mark this request as the latest
    const reqId = ++latestReqIdRef.current;

    const baselineRevisionId = String(
      selectedRevisionId ||
      currentRevisionIdFromJob(pipelineJob) ||
      ""
    )
      .trim()
      .toUpperCase();

    revisionBaselineRef.current = baselineRevisionId;
    expectedRevisionRef.current = "";
    setPipelineMode("revision");
    setPipelineError("");
    setPipelineMessageOverride("Submitting your revision request…");
    setPipelineLoading(true);
    setPipelineVisible(true);

    try {
      setLoading(false);

      // Collect the original Start-page source URLs retained for revision context
      const allUrls = [];
      for (const s of sources || []) {
        if (Array.isArray(s?.fileUrls)) allUrls.push(...s.fileUrls);
        if (s?.fileUrl) allUrls.push(s.fileUrl);
        if (s?.url) allUrls.push(s.url);
      }
      const fileUrls = Array.from(new Set(allUrls.filter(Boolean)));

      // Preserve the document's original category invisibly; subject selection is
      // intentionally unavailable on the Editor page.
      const effectiveCategory =
        (changeCategory && changeCategory.trim()) ? changeCategory.trim() : "Other / Not Listed";

      const instructionText = buildRevisionInstruction({
        subject: session.subjectName || session.title || "",
        category: effectiveCategory,
        tone: changeTone,
        audience: changeAudience,
        readingLevel: changeReadingLevel,
        language: changeLanguage,
        fields: fieldValues || {},
        fileUrls,
      });

      const effectiveLengthPreference =
        fieldValues?.lengthPreference || { mode: "auto" };

      // Source metadata is sent invisibly with the revision request. Audience is
      // inherited from D1; all user-changeable revision selections are also carried
      // here so none of the Editor controls depends only on UI state.
      const attachment = {
        fileUrls,
        meta: {
          via: "editor",
          previousRevision: uiRevisionsUsed,
          category: effectiveCategory,
          audience: changeAudience || "",
          tone: changeTone || "formal",
          readingLevel: changeReadingLevel || "standard",
          language: changeLanguage || "auto",
          lengthPreference: effectiveLengthPreference,
        },
      };

      // Queue one JSON-only AI revision operation. The ordered instruction remains
      // authoritative for the model, while the explicit fields make the transport
      // contract unambiguous for Tone, Reading Level, Language, Details, and Length.
      const res = await api.applyRevision({
        jobId,
        sessionId: session.sessionId,
        text: instructionText,
        revisionInstruction: instructionText,
        attachment,
        baseRevisionId: baselineRevisionId || undefined,
        tone: changeTone || "formal",
        readingLevel: changeReadingLevel || "standard",
        language: changeLanguage || "auto",
        details,
        lengthPreference: effectiveLengthPreference,
      });

      // If a newer request finished after this one, ignore this stale response
      if (reqId !== latestReqIdRef.current) {
        console.warn("[DocWriterEditorPage] Stale revision response ignored");
        return;
      }

      const queuedJob = safeObject(res?.job || res?.data?.job || res);
      const targetRevisionId = String(
        firstValue(
          targetRevisionIdFromJob(queuedJob),
          res?.targetRevisionId,
          res?.operation?.targetRevisionId
        )
      )
        .trim()
        .toUpperCase();

      if (targetRevisionId) {
        expectedRevisionRef.current = targetRevisionId;
      }

      if (Object.keys(queuedJob).length) {
        setPipelineJob(queuedJob);
      }

      setPipelineMessageOverride(
        targetRevisionId
          ? `${targetRevisionId} is queued. The editor will reopen automatically when it is ready.`
          : "Your revision is queued. The editor will reopen automatically when it is ready."
      );


      setTermsAccepted(false);
      setRevisionValidationErrors({});
      setFieldValues((previous) => ({
        ...previous,
        details: "",
        lengthPreference: previous.lengthPreference || { mode: "auto" },
      }));
      addToast("Revision queued.", "success");
    } catch (e) {
      // Ignore errors from stale requests if a newer one completed
      if (reqId === latestReqIdRef.current) {
        console.error("[DocWriterEditorPage] ❌ ApplyRevision failed:", e);
        setPipelineVisible(false);
        setPipelineLoading(false);
        setPipelineMessageOverride("");
        setPipelineError("");
        addToast(DOCWRITER_GENERIC_USER_ERROR, "error");
      }
    } finally {
      if (reqId === latestReqIdRef.current) setLoading(false);
    }
  };

  // ---------- Preview / View / Restore ----------
  const handleViewRevision = async (revisionId) => {
    try {
      const normalizedRevisionId = normalizeRevisionId(revisionId);
      const revRes = await api.getRevision({
        jobId,
        sessionId: session.sessionId,
        revisionId: normalizedRevisionId,
      });
      const rev = revRes?.data ?? revRes;
      const contentHtml = await readHtmlFromLoadedSession(rev);
      if (!contentHtml) {
        addToast("No content in this revision.", "info");
        return;
      }
      // Enter PREVIEW: remember current unsaved draft; show viewed HTML; disable autosave.
      if (!previewingId) {
        prePreviewHtmlRef.current =
          readLiveCanvasDocument("save").html || localHtml;
      }
      setPreviewingId(normalizedRevisionId);
      setSelectedRevisionId(normalizedRevisionId);
      replaceCanvasDocument(contentHtml);

      // Cache the exact fetched HTML into the in-memory revisions list.
      setRevisions((prev) =>
        prev.map((r) =>
          normalizeRevisionId(r.id) === normalizedRevisionId
            ? { ...r, id: normalizedRevisionId, html: contentHtml }
            : r
        )
      );

      // ✅ Keep the "viewing past revision (read-only)" message, remove "Exit Preview" phrasing
      addToast("Viewing past revision (read-only).", "info");
    } catch (e) {
      console.error("[DocWriterEditorPage] ❌ getRevision failed:", e);
      addToast("The document version could not be opened. Please try again.", "error");
    }
  };

  // Exit Preview UI removed; function retained (not rendered) for possible programmatic use
  const exitPreview = () => {
    if (!previewingId) return;
    setPreviewingId(null);
    // Restore the previously edited draft
    const restored = prePreviewHtmlRef.current || session.contentHtml || "";
    replaceCanvasDocument(restored);
    setDirty(restored !== lastSavedHtml);
    addToast("Exited preview.", "success");
  };

  const handleRestoreRevision = async (revisionId) => {
    if (session.status === "locked") {
      addToast("This document is finalized and read-only.", "error");
      return;
    }

    const normalizedRevisionId = normalizeRevisionId(revisionId);

    try {
      setLoading(true);

      // Restore is a safe reload of the exact saved backend revision. The
      // authoritative routes expose revision retrieval, not a restore mutation.
      const revRes = await api.getRevision({
        jobId,
        sessionId: session.sessionId,
        revisionId: normalizedRevisionId,
      });
      const rev = revRes?.data ?? revRes;
      const restoredHtml = await readHtmlFromLoadedSession(rev);

      if (!restoredHtml) {
        addToast("No saved content was found for this revision.", "error");
        return;
      }

      const revisionRecord = safeObject(rev?.revision);
      const restoredTitle =
        String(
          firstValue(
            rev?.title,
            revisionRecord?.title,
            revisionRecord?.label,
            session.title
          )
        ).trim() || session.title;

      // Discard unsaved canvas edits and return to the selected saved version.
      setPreviewingId(null);
      prePreviewHtmlRef.current = "";
      setSelectedRevisionId(normalizedRevisionId);
      selectedRevisionIdRef.current = normalizedRevisionId;
      replaceCanvasDocument(restoredHtml);
      setSession((prev) => ({
        ...prev,
        contentHtml: restoredHtml,
        title: restoredTitle,
      }));

      // Cache the exact restored HTML without creating or counting a revision.
      setRevisions((prev) =>
        prev.map((item) =>
          normalizeRevisionId(item.id) === normalizedRevisionId
            ? { ...item, id: normalizedRevisionId, html: restoredHtml }
            : item
        )
      );

      setLastSavedHtml(restoredHtml);
      setLastSavedTitle(restoredTitle);
      setDirty(false);
      addToast("Saved version restored. Unsaved edits were discarded.", "success");
    } catch (e) {
      console.error("[DocWriterEditorPage] ❌ revision restore reload failed:", e);
      addToast("The saved document version could not be restored. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // ---------- Print / Download helpers ----------
  const saveBeforeAction = async () => {
    if (previewingId) return; // don't persist preview content
    try {
      const prepared = readLiveCanvasDocument("save");
      const exactHtml = String(prepared.html || "");
      if (!exactHtml.trim()) return;

      await api.saveContent({
        sessionId: session.sessionId,
        contentHtml: exactHtml,
        title: session.title,
      });
      setLocalHtml(exactHtml);
      setLastSavedHtml(exactHtml);
      setLastSavedTitle(session.title);
      setDirty(false);
    } catch {}
  };

  const handlePrint = async () => {
    addToast("Heads up: Print does not finalize. Use Finalize to lock the document.", "info");
    await saveBeforeAction();
    setTimeout(() => window.print(), 0);
  };

  const handleDownload = async (format /* "pdf" | "docx" | "text" */) => {
    await saveBeforeAction();
    try {
      const blob = await api.exportDoc({
        sessionId: session.sessionId,
        format,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${session.title || "document"}.${
        format === "text" ? "txt" : format
      }`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (format === "text") {
        const tmp = document.createElement("div");
        tmp.innerHTML = localHtml || "";
        const text = tmp.innerText || tmp.textContent || "";
        const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${session.title || "document"}.txt`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        addToast("Downloaded as .txt (frontend fallback).", "success");
        return;
      }
      if (format === "pdf") {
        addToast("Use Print → Save as PDF for a quick PDF export.", "info");
        setTimeout(() => window.print(), 0);
        return;
      }
      if (format === "docx") {
        const htmlDoc = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${
          session.title || "document"
        }</title></head><body>${localHtml || ""}</body></html>`;
        const blob = new Blob([htmlDoc], { type: "application/msword" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${session.title || "document"}.doc`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        addToast("Downloaded as .doc (frontend fallback).", "success");
        return;
      }
      addToast("The download could not be completed. Please try again.", "error");
    }
  };

  const handlePreviewExactRevision = async (revisionId) => {
    const exactRevisionId = normalizeRevisionId(revisionId);

    if (!exactRevisionId) {
      throw new Error("A valid Doc Writer revision is required for PDF preview.");
    }

    if (!jobId || !session.sessionId) {
      throw new Error("The Doc Writer job or session identity is missing.");
    }

    if (pdfPreviewingId) {
      throw new Error("A PDF preview is already being prepared.");
    }

    setPdfPreviewingId(exactRevisionId);

    try {
      const selectedId = normalizeRevisionId(
        selectedRevisionIdRef.current || selectedRevisionId
      );

      let exactHtml = "";
      let exactText = "";

      if (selectedId === exactRevisionId) {
        const prepared = readLiveCanvasDocument("preview");
        exactHtml = String(prepared.html || "");
        exactText = String(prepared.text || "");
      } else {
        const revisionResponse = await api.getRevision({
          jobId,
          sessionId: session.sessionId,
          revisionId: exactRevisionId,
        });
        const revisionData = revisionResponse?.data ?? revisionResponse;
        exactHtml = String(
          (await readHtmlFromLoadedSession(revisionData)) || ""
        );
        exactText = String(
          revisionData?.contentText ||
            revisionData?.textContent ||
            revisionData?.revision?.textContent ||
            revisionData?.revision?.contentText ||
            ""
        );
      }

      if (!exactHtml.trim()) {
        throw new Error("The selected revision has no content to preview.");
      }

      if (!exactText.trim()) {
        exactText = plainDocumentTextFromHtml(exactHtml);
      }

      const preview = await api.previewPdf({
        jobId,
        sessionId: session.sessionId,
        revisionId: exactRevisionId,
        contentHtml: exactHtml,
        contentText: exactText,
        title: session.title,
        subject: session.subjectName || session.title || "",
        metadata: {
          source: "revision_history",
          selectedRevisionId: exactRevisionId,
        },
      });

      const pdfBlob = preview?.blob;

      if (
        typeof Blob === "undefined" ||
        !(pdfBlob instanceof Blob) ||
        pdfBlob.size <= 0
      ) {
        throw new Error("The PDF preview could not be opened. Please try again.");
      }

      const baseTitle = String(session.title || "Document").trim() || "Document";
      setPdfViewerRecord({
        id: `docwriter-preview-${session.sessionId}-${exactRevisionId}-${Date.now()}`,
        title: `${baseTitle} — ${exactRevisionId} PDF Preview`,
        subject: `${baseTitle} — ${exactRevisionId} PDF Preview`,
        filename: `${baseTitle} ${exactRevisionId} Preview.pdf`,
        contentType: "application/pdf",
        format: "pdf",
        pdfBlob,
        allowDownload: true,
        createdAt: new Date().toISOString(),
      });
      setPdfViewerOpen(true);

      addToast(
        `${exactRevisionId} PDF preview opened in the in-app viewer.`,
        "success"
      );

      return preview;
    } catch (error) {
      console.error(
        "[DocWriterEditorPage] exact PDF preview failed:",
        error
      );
      throw error;
    } finally {
      setPdfPreviewingId("");
    }
  };

  const handleFinalizeExactRevision = async (revisionId, format) => {
    const exactRevisionId = normalizeRevisionId(revisionId);
    const exactFormat = String(format || "").trim().toLowerCase();

    if (!exactRevisionId) {
      throw new Error("A valid Doc Writer revision is required.");
    }

    if (!["pdf", "docx", "text"].includes(exactFormat)) {
      throw new Error("A valid final document format is required.");
    }

    if (!jobId || !session.sessionId) {
      throw new Error("The Doc Writer job or session identity is missing.");
    }

    if (session.status === "locked") {
      throw new Error("This document is already finalized and read-only.");
    }

    const prepared = readLiveCanvasDocument("finalize");
    const exactHtml = String(prepared.html || "");
    const exactText = String(prepared.text || "");

    if (!exactHtml.trim()) {
      throw new Error("The selected revision has no content to finalize.");
    }

    try {
      setLoading(true);

      // Persist the exact clicked D1/R* revision before locking it.
      await api.saveExactRevision({
        sessionId: session.sessionId,
        revisionId: exactRevisionId,
        contentHtml: exactHtml,
        contentText: exactText,
        title: session.title,
        makeCurrent: true,
      });

      // The backend finalization route owns the permanent lock, finalized
      // artifact creation, File Vault injection, and Doc History update.
      const finalized = await api.publishNow({
        jobId,
        sessionId: session.sessionId,
        revisionId: exactRevisionId,
        contentHtml: exactHtml,
        format: exactFormat,
        subject: session.subjectName || session.title || "",
      });

      selectedRevisionIdRef.current = exactRevisionId;
      setSelectedRevisionId(exactRevisionId);
      setPreviewingId(null);
      setLocalHtml(exactHtml);
      setLastSavedHtml(exactHtml);
      setLastSavedTitle(session.title);
      setDirty(false);

      setRevisions((previous) =>
        previous.map((revision) =>
          normalizeRevisionId(revision.id) === exactRevisionId
            ? {
                ...revision,
                html: exactHtml,
                type: "final",
              }
            : revision
        )
      );

      setSession((previous) => ({
        ...previous,
        status: "locked",
        contentHtml: exactHtml,
      }));
      setSubjectLocked(true);

      addToast("Document finalized and locked.", "success");

      navigate("/docwriter/history", {
        replace: true,
        state: {
          justFinalized: true,
          jobId,
          sessionId: session.sessionId,
          revisionId: exactRevisionId,
          format: exactFormat,
          finalized:
            finalized?.finalized ??
            finalized?.job?.finalized ??
            true,
        },
      });

      return finalized;
    } catch (error) {
      console.error(
        "[DocWriterEditorPage] exact revision finalization failed:",
        error
      );
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleTitleChange = (newTitle) => {
    setSession((prev) => ({ ...prev, title: newTitle || "Untitled Document" }));
    if (!hydrated || previewingId) return;
    // Mark title change as a reason to save (explicit save only; no autosave)
    setDirty((d) => d || (newTitle || "Untitled Document") !== lastSavedTitle);
  };

  // Load one selected version immediately, then confirm that exact D/R pointer
  // from the backend. A request sequence prevents a slower prior click from
  // replacing a newer tab selection.
  const loadRevisionForEditing = async (revisionId, { focus = false } = {}) => {
    const normalizedRevisionId = normalizeRevisionId(revisionId);
    if (!normalizedRevisionId) return;

    const requestId = ++revisionLoadRequestRef.current;
    selectedRevisionIdRef.current = normalizedRevisionId;
    setSelectedRevisionId(normalizedRevisionId);
    setPreviewingId(null);
    setEditorOpen(true);

    const placeInEditor = (contentHtml) => {
      if (
        requestId !== revisionLoadRequestRef.current ||
        selectedRevisionIdRef.current !== normalizedRevisionId
      ) {
        return;
      }

      const editableHtml = normalizeModelOutputToHtml(contentHtml || "");
      replaceCanvasDocument(editableHtml);
      setLastSavedHtml(editableHtml);
      setDirty(false);

      if (focus) {
        setTimeout(() => {
          editorRef.current?.focusEditor?.();
          editorRef.current?.focus?.();
        }, 0);
        setFocusNonce((n) => n + 1);
      }
    };

    const cached = revisions.find(
      (revision) => normalizeRevisionId(revision.id) === normalizedRevisionId
    );

    // Render cached content immediately, then replace it only with the exact
    // selected revision response.
    if (typeof cached?.html === "string" && cached.html.trim()) {
      placeInEditor(cached.html);
    }

    setRevisionSwitchingId(normalizedRevisionId);

    try {
      const revRes = await api.getRevision({
        jobId,
        sessionId: session.sessionId,
        revisionId: normalizedRevisionId,
      });

      if (
        requestId !== revisionLoadRequestRef.current ||
        selectedRevisionIdRef.current !== normalizedRevisionId
      ) {
        return;
      }

      const rev = revRes?.data ?? revRes;
      const returnedRevisionId = normalizeRevisionId(
        firstValue(
          rev?.requestedRevisionId,
          rev?.revisionId,
          safeObject(rev?.revision).revisionId
        )
      );

      if (returnedRevisionId && returnedRevisionId !== normalizedRevisionId) {
        throw new Error(
          `The server returned ${returnedRevisionId} instead of ${normalizedRevisionId}.`
        );
      }

      const exactHtml = await readHtmlFromLoadedSession(rev);
      if (!exactHtml) {
        throw new Error(`No readable content was returned for ${normalizedRevisionId}.`);
      }

      if (
        requestId !== revisionLoadRequestRef.current ||
        selectedRevisionIdRef.current !== normalizedRevisionId
      ) {
        return;
      }

      setRevisions((prev) =>
        prev.map((revision) =>
          normalizeRevisionId(revision.id) === normalizedRevisionId
            ? { ...revision, id: normalizedRevisionId, html: exactHtml }
            : revision
        )
      );
      placeInEditor(exactHtml);
    } catch (e) {
      if (
        requestId === revisionLoadRequestRef.current &&
        selectedRevisionIdRef.current === normalizedRevisionId
      ) {
        console.error("[DocWriterEditorPage] ❌ exact revision load failed:", e);
        addToast(
          "The selected document version could not be opened. Please try again.",
          "error"
        );
      }
    } finally {
      if (
        requestId === revisionLoadRequestRef.current &&
        selectedRevisionIdRef.current === normalizedRevisionId
      ) {
        setRevisionSwitchingId("");
      }
    }
  };

  // 🔧 Handle "Edit" from Revision History: load that exact version and focus the canvas.
  const handleRequestEdit = (revId) => {
    loadRevisionForEditing(revId, { focus: true });
  };

  // ---------- Required revision details ----------
  const schema = useMemo(
    () => [
      {
        id: "details",
        label: "Key Facts / Details",
        type: "textarea",
        required: true,
        placeholder: "Describe the exact changes you want in this revision…",
      },
      {
        id: "lengthPreference",
        label: "Length Preference",
        type: "length",
        required: true,
      },
    ],
    []
  );

  // ---------- Render ----------
  const normalizedPipelineStatus = normalizeDocwriterStatus(pipelineJob);
  const pipelineMeta = docwriterStageMeta(pipelineJob);
  const pipelineActiveIndex = docwriterCompactStageIndex(pipelineJob);
  const pipelineProgress = safeObject(pipelineJob?.progress);
  const pipelineIsRevision =
    pipelineMode === "revision" || isRevisionOperation(pipelineJob);
  const pipelineOperationLabel = pipelineIsRevision ? "Revising" : "Drafting";
  const pipelineWritingLabel = pipelineIsRevision ? "Revising" : "Drafting";
  const pipelineStageLabel =
    pipelineIsRevision && normalizedPipelineStatus === "drafting"
      ? "Revising document"
      : pipelineMeta.label;
  const pipelineUserNotice = docwriterUserNotice(pipelineJob);
  const pipelineIsSourceRejection =
    normalizedPipelineStatus === "abandoned" ||
    pipelineUserNotice.kind === "source_rejection";
  const pipelineIsError =
    normalizedPipelineStatus === "error" ||
    normalizedPipelineStatus === "awaiting_source_correction" ||
    normalizedPipelineStatus === "abandoned";
  const pipelineIsReady =
    normalizedPipelineStatus === "ready" ||
    normalizedPipelineStatus === "locked";

  const explicitProgress = firstValue(
    pipelineProgress.percent,
    pipelineProgress.progressPercent,
    pipelineJob?.progressPercent,
    safeObject(pipelineJob?.activeOperation).progressPercent
  );
  const pipelinePercent = clampPercent(
    explicitProgress,
    pipelineIsReady ? 100 : pipelineMeta.percent
  );

  const pipelineTargetRevisionId =
    expectedRevisionRef.current ||
    targetRevisionIdFromJob(pipelineJob) ||
    (pipelineIsRevision ? "Next revision" : "D1");

  const pipelineMessage =
    pipelineMessageOverride ||
    (pipelineIsError
      ? String(pipelineUserNotice.message || "").trim() ||
        (pipelineIsSourceRejection
          ? "One or more supporting files could not be accepted."
          : DOCWRITER_GENERIC_USER_ERROR)
      : pipelineProgress.message ||
        (pipelineIsRevision && normalizedPipelineStatus === "drafting"
          ? "Lexa is revising the selected document version."
          : pipelineMeta.text));

  if (pipelineVisible) {
    return (
      <div
        className="doc-writer-editor-page-root doc-writer-pipeline-page"
        style={{ ...s.page, ...(isSmall ? s.pageMobile : null) }}
      >
        <style>{`
          @keyframes docWriterPipelineSpin {
            to { transform: rotate(360deg); }
          }

          .doc-writer-pipeline-page,
          .doc-writer-pipeline-page * {
            box-sizing: border-box;
          }

          @media (max-width: 1023px) {
            .doc-writer-pipeline-hero {
              display: block !important;
            }

            .doc-writer-pipeline-status-pill {
              margin-top: 12px !important;
              width: 100% !important;
            }

            .doc-writer-pipeline-stage-grid {
              grid-template-columns: repeat(5, minmax(0, 1fr)) !important;
              gap: 8px !important;
            }
          }

          @media (max-width: 760px) {
            .doc-writer-pipeline-stage-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }
          }

          @media (max-width: 620px) {
            .doc-writer-pipeline-top {
              display: block !important;
            }

            .doc-writer-pipeline-job-badge {
              margin-top: 10px !important;
            }

            .doc-writer-pipeline-processing-head {
              align-items: flex-start !important;
            }

            .doc-writer-pipeline-segment-labels {
              gap: 4px !important;
              font-size: 9.5px !important;
            }
          }

          @media (max-width: 420px) {
            .doc-writer-pipeline-segment-labels {
              font-size: 9px !important;
            }
          }
        `}</style>

        <div style={s.pipelineShell}>
          <header
            className="doc-writer-pipeline-hero"
            style={s.pipelineHero}
          >
            <div style={s.pageHeaderText}>
              <div
                style={{
                  ...s.eyebrow,
                  ...(isSmall ? s.eyebrowMobile : null),
                }}
              >
                Doc Writer
              </div>
              <h1
                style={{
                  ...s.h1,
                  ...(isSmall ? s.h1Mobile : null),
                }}
              >
                {pipelineIsRevision
                  ? `Revising ${pipelineTargetRevisionId}`
                  : "Drafting D1"}
              </h1>
              <p
                style={{
                  ...s.subtitle,
                  ...(isSmall ? s.subtitleMobile : null),
                }}
              >
                {pipelineIsRevision
                  ? "Your selected version is protected while the next revision is created."
                  : "Your editor will open automatically when D1 is saved and readable."}
              </p>
            </div>

            <div
              className="doc-writer-pipeline-status-pill"
              style={{
                ...s.pipelineStatusPill,
                borderColor: `${pipelineMeta.color}33`,
                background: pipelineIsError
                  ? "#FEF2F2"
                  : pipelineIsReady
                  ? "#ECFDF5"
                  : "#FFFFFF",
                color: pipelineMeta.color,
              }}
            >
              {!pipelineIsError && !pipelineIsReady ? (
                <Spinner size={15} color={pipelineMeta.color} />
              ) : (
                <span
                  style={{
                    width: 9,
                    height: 9,
                    borderRadius: 999,
                    background: pipelineMeta.color,
                  }}
                />
              )}
              <span>{pipelineStageLabel}</span>
            </div>
          </header>

          <section style={s.pipelinePanel}>
            <div
              className="doc-writer-pipeline-top"
              style={s.pipelineTop}
            >
              <div>
                <h2 style={s.pipelineTitle}>
                  {pipelineOperationLabel} progress
                </h2>
                <p style={s.pipelineCopy}>
                  {pipelineLoading
                    ? "Loading the latest Doc Writer status…"
                    : pipelineMessage}
                </p>
              </div>

              <div
                className="doc-writer-pipeline-job-badge"
                style={{
                  ...s.pipelineJobBadge,
                  color: pipelineMeta.color,
                  borderColor: `${pipelineMeta.color}33`,
                  background: `${pipelineMeta.color}0D`,
                }}
              >
                {pipelineStageLabel} • {pipelineTargetRevisionId}
              </div>
            </div>

            {!pipelineIsError ? (
              <div style={s.processingNote}>
                <div
                  className="doc-writer-pipeline-processing-head"
                  style={s.processingNoteHead}
                >
                  <div style={s.processingNoteLead}>
                    <span style={s.processingNoteIcon}>✓</span>
                    <strong style={s.processingNoteTitle}>
                      Your document is {pipelineOperationLabel.toLowerCase()} in the background.
                    </strong>
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      setShowProcessingDetails((value) => !value)
                    }
                    style={s.processingNoteToggle}
                    aria-expanded={showProcessingDetails}
                  >
                    {showProcessingDetails ? "Hide details" : "Show details"}
                  </button>
                </div>

                {showProcessingDetails ? (
                  <p style={s.processingNoteText}>
                    You may safely leave this page and return through Doc Writer
                    history. The editor remains hidden while the active operation
                    is running so an unfinished draft or revision cannot be edited
                    or saved accidentally.
                  </p>
                ) : null}
              </div>
            ) : null}

            <div style={s.instrumentPanel}>
              <div style={s.instrumentTop}>
                <div>
                  <div style={s.instrumentLabel}>Overall progress</div>
                  <div style={s.instrumentSub}>
                    {pipelineLoading
                      ? "Loading job status…"
                      : `${Math.round(pipelinePercent)}% complete`}
                  </div>
                </div>
                <div
                  style={{
                    ...s.instrumentPercent,
                    color: pipelineMeta.color,
                  }}
                >
                  {Math.round(pipelinePercent)}%
                </div>
              </div>

              <div style={s.segmentTrack}>
                <div
                  style={{
                    ...s.segmentFill,
                    width: `${pipelinePercent}%`,
                    background: `linear-gradient(90deg, #2563EB, ${pipelineMeta.color})`,
                  }}
                />
              </div>

              <div
                className="doc-writer-pipeline-segment-labels"
                style={s.segmentLabels}
              >
                <span>Received</span>
                <span>Reading</span>
                <span>Summarizing</span>
                <span>{pipelineWritingLabel}</span>
                <span>Saving</span>
                <span>Ready</span>
              </div>
            </div>

            <div
              className="doc-writer-pipeline-stage-grid"
              style={s.stageGrid}
              aria-label={`${pipelineOperationLabel} progress stages`}
            >
              {DOCWRITER_COMPACT_STATUS_STEPS.map((step, index) => {
                const label =
                  step.key === "writing" ? pipelineWritingLabel : step.label;
                const done = pipelineIsReady || pipelineActiveIndex > index;
                const current =
                  pipelineActiveIndex === index &&
                  !pipelineIsReady &&
                  !pipelineIsError;
                const pending = !done && !current;

                return (
                  <div
                    key={step.key}
                    style={{
                      ...s.stageCard,
                      ...(done ? s.stageDone : null),
                      ...(current ? s.stageCurrent : null),
                      ...(pending ? s.stagePending : null),
                      borderColor:
                        done || current
                          ? `${step.color}44`
                          : "rgba(15,23,42,0.08)",
                      boxShadow: current
                        ? `0 0 0 2px ${step.color}18, 0 14px 28px rgba(15,23,42,0.08)`
                        : "none",
                    }}
                  >
                    <div style={s.stageTop}>
                      <span
                        style={{
                          ...s.stageIcon,
                          background: done || current ? step.color : "#E2E8F0",
                          color: done || current ? "#FFFFFF" : "#64748B",
                        }}
                      >
                        {done ? "✓" : index + 1}
                      </span>
                      {current ? <Spinner size={13} color={step.color} /> : null}
                    </div>
                    <div style={s.stageLabel}>{label}</div>
                  </div>
                );
              })}
            </div>

            {pipelineError ? (
              <div
                style={{
                  ...s.pipelineNotice,
                  background:
                    pipelineIsSourceRejection
                      ? "#FFFBEB"
                      : "#FEF2F2",
                  border:
                    pipelineIsSourceRejection
                      ? "1px solid rgba(217,119,6,0.22)"
                      : "1px solid rgba(220,38,38,0.18)",
                  color:
                    pipelineIsSourceRejection
                      ? "#92400E"
                      : "#991B1B",
                }}
              >
                <strong>
                  {pipelineIsSourceRejection
                    ? "Source file needs attention:"
                    : "Something went wrong:"}
                </strong>{" "}
                {pipelineError}

                <div style={s.pipelineActions}>
                  {!pipelineIsSourceRejection ? (
                    <button
                      type="button"
                      style={s.pipelineButton}
                      onClick={() => {
                        setPipelineError("");
                        setPipelineLoading(true);
                        setPipelinePollNonce((value) => value + 1);
                      }}
                    >
                      Try again
                    </button>
                  ) : null}
                  <button
                    type="button"
                    style={
                      pipelineIsSourceRejection
                        ? s.pipelineButton
                        : s.pipelineSecondaryButton
                    }
                    onClick={() => navigate("/docwriter/start")}
                  >
                    {pipelineIsSourceRejection
                      ? "Start Over"
                      : "Return to Doc Writer"}
                  </button>
                </div>
              </div>
            ) : null}
          </section>
        </div>

        <div style={s.toastWrap}>
          {toasts.map((toast) => (
            <div key={toast.id} style={s.toast(toast.type)}>
              {toast.message}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className="doc-writer-editor-page-root"
      style={{ ...s.page, ...(isSmall ? s.pageMobile : null) }}
    >
      {/* Print: Letter with 1in margins, limit output to the tagged editor area */}
      <style>{`
        /* Page-level border-box applies everywhere except inside DocWriterMath.
           KaTeX/DocWriterMath owns the complete internal box model so Canvas matches
           the component Live Preview instead of inheriting page geometry rules. */
        .doc-writer-editor-page-root *:not(.docwriter-math-shell):not(.docwriter-math-shell *):not([data-doc-writer-table="true"]):not([data-doc-writer-table="true"] *) {
          box-sizing: border-box;
        }
        .doc-writer-editor-page-root button,
        .doc-writer-editor-page-root input,
        .doc-writer-editor-page-root select,
        .doc-writer-editor-page-root textarea { font: inherit; }
        .doc-writer-editor-page-root a {
          color: #2563EB;
          font-weight: 900;
          text-decoration: none;
        }
        .doc-writer-editor-page-root a:hover { text-decoration: underline; }
        @keyframes docWriterPipelineSpin {
          to { transform: rotate(360deg); }
        }
        .doc-writer-revision-tab::before {
          content: "";
          position: absolute;
          top: -7px;
          left: 12px;
          width: 29px;
          height: 8px;
          border: 1px solid var(--docwriter-tab-border);
          border-bottom: 0;
          border-radius: 8px 8px 0 0;
          background: var(--docwriter-tab-bg);
          z-index: -1;
        }
        .doc-writer-revision-tab:hover {
          transform: translateY(-2px) !important;
          filter: saturate(1.06) !important;
        }
        .doc-writer-revision-tab[aria-selected="true"]:hover {
          transform: translateY(0) !important;
        }
        .doc-writer-revision-tabs-dock::-webkit-scrollbar {
          width: 0;
          height: 0;
        }
        .doc-writer-editor-page-root button:focus-visible,
        .doc-writer-editor-page-root input:focus-visible,
        .doc-writer-editor-page-root select:focus-visible,
        .doc-writer-editor-page-root textarea:focus-visible {
          outline: 3px solid rgba(37,99,235,0.22);
          outline-offset: 2px;
        }
        .doc-writer-modern-fields input:not([type="checkbox"]):not([type="radio"]),
        .doc-writer-modern-fields select,
        .doc-writer-modern-fields textarea {
          width: 100% !important;
          max-width: 100% !important;
          border: 1px solid rgba(15,23,42,0.16) !important;
          border-radius: 12px !important;
          background: #FFFFFF !important;
          color: #0F172A !important;
          padding: 11px 12px !important;
          box-shadow: none !important;
        }
        .doc-writer-modern-fields textarea {
          min-height: 108px;
          resize: vertical;
        }
        .doc-writer-modern-fields label {
          color: #0F172A !important;
          font-weight: 850 !important;
        }
        .doc-writer-modern-fields button {
          border-radius: 10px !important;
        }
        .doc-writer-modern-fields .doc-writer-editor-collapse-button {
          border-radius: 13px !important;
        }
        /* The page must never suppress DocCanvasEditor's own workspace scrolling.
           Do not give this shell a competing vertical scroller; only ensure the
           child workspace remains allowed to scroll itself. */
        .doc-writer-editor-canvas-shell {
          overflow: visible !important;
          height: auto !important;
          max-height: none !important;
          overscroll-behavior: auto !important;
        }
        .doc-writer-editor-canvas-shell .doc-canvas-workspace {
          overflow-x: auto !important;
          overflow-y: auto !important;
          scrollbar-gutter: stable !important;
          overscroll-behavior: contain !important;
          -webkit-overflow-scrolling: touch;
          pointer-events: auto;
        }

        html, body, #root {
          max-width: 100%;
          overflow-x: hidden;
        }
        .doc-writer-editor-page-root,
        .doc-writer-editor-page-root *:not([data-doc-writer-table="true"]):not([data-doc-writer-table="true"] *),
        .doc-writer-editor-page-grid,
        .doc-writer-editor-workspace-panel {
          min-width: 0;
          max-width: 100%;
        }
        @media (max-width: 1023px) {
          .doc-writer-editor-page-root {
            padding: 14px 8px 34px !important;
          }
          .doc-writer-editor-workspace-panel {
            border-radius: 14px !important;
          }
          .doc-writer-editor-section-pad {
            padding: 12px !important;
          }
          .doc-writer-editor-canvas-shell {
            padding: 8px 4px 12px !important;
            scrollbar-gutter: auto !important;
          }
          .doc-writer-editor-apply-bar {
            padding: 0 12px 14px !important;
          }
          .doc-writer-editor-collapse-button {
            width: 38px !important;
            height: 38px !important;
            min-width: 38px !important;
            flex-basis: 38px !important;
            border-radius: 13px !important;
            font-size: 21px !important;
          }
        }
        @media (max-width: 720px) {
          .doc-writer-revision-tabs-dock {
            left: 10px !important;
            right: 10px !important;
            max-width: calc(100% - 20px) !important;
            justify-content: flex-start !important;
          }
          .doc-writer-revision-tab {
            min-width: 58px !important;
            padding-left: 13px !important;
            padding-right: 13px !important;
          }
        }
        @media print {
          @page { size: Letter; margin: 1in; }
          body * { visibility: hidden !important; }
          #${PRINT_TAG_ID}, #${PRINT_TAG_ID} * { visibility: visible !important; }
          #${PRINT_TAG_ID} { position: absolute; left: 0; top: 0; width: 100%; }
          .doc-page-break { page-break-after: always; break-after: page; }
        }
        /* caret/typing stability inside editor.
           Apply the typing guard to normal editable document content only.
           Never push direction/text-align/white-space overrides into DocWriterMath/KaTeX
           or DocWriterTable; each component owns its own presentation subtree. */
        .editor-typing-wrap [contenteditable="true"] {
          direction: ltr !important;
          unicode-bidi: plaintext !important;
          text-align: left !important;
          white-space: pre-wrap;
        }
        .editor-typing-wrap [contenteditable="true"]
          *:not(.docwriter-math-shell):not(.docwriter-math-shell *):not([data-doc-writer-table="true"]):not([data-doc-writer-table="true"] *) {
          direction: ltr !important;
          unicode-bidi: plaintext !important;
          text-align: left !important;
          white-space: pre-wrap;
        }
      `}</style>

      <header
        style={{
          ...s.pageHeader,
          marginBottom: introCollapsed ? 8 : s.pageHeader.marginBottom,
        }}
      >
        <div style={s.pageHeaderTop}>
          <div
            style={{
              ...s.eyebrow,
              ...(isSmall ? s.eyebrowMobile : null),
              marginBottom: 0,
              textAlign: "left",
            }}
          >
            Doc Writer
          </div>
          <button
            type="button"
            className="doc-writer-editor-collapse-button"
            style={s.collapseButton}
            aria-expanded={!introCollapsed}
            aria-controls="doc-writer-editor-intro-content"
            aria-label={introCollapsed ? "Show page introduction" : "Hide page introduction"}
            title={introCollapsed ? "Show page introduction" : "Hide page introduction"}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => setIntroCollapsed((current) => !current)}
          >
            {introCollapsed ? "+" : "−"}
          </button>
        </div>

        <div
          id="doc-writer-editor-intro-content"
          style={s.collapsibleContent(introCollapsed)}
        >
          <div style={{ ...s.pageHeaderText, marginTop: 10 }}>
            <h1 style={{ ...s.h1, ...(isSmall ? s.h1Mobile : null) }}>
              Review and revise your document
            </h1>
            <p
              style={{
                ...s.subtitle,
                ...(isSmall ? s.subtitleMobile : null),
              }}
            >
              Open a version, review its content, make manual edits, request a new
              revision, save your work, or finalize the document when complete.
            </p>
          </div>
        </div>
      </header>

      <div
        className="doc-writer-editor-page-grid"
        style={{
          ...s.pageGrid,
          ...(isSmall ? s.pageGridMobile : null),
        }}
      >
        <main style={s.mainColumn}>
          {/* ===== Existing Editor container: functionality and controls preserved ===== */}
          <section
            className="doc-writer-editor-workspace-panel"
            style={{ ...s.pane, ...s.editorPaneWithTabs }}
          >
            <div
              className="doc-writer-revision-tabs-dock"
              style={s.revisionTabsDock}
              role="tablist"
              aria-label="Document versions"
            >
              {revisionTabs.map((revision, index) => {
                const revisionId = normalizeRevisionId(revision.id);
                const tabLabel = revisionId || "Version";
                const active = revisionId === normalizeRevisionId(selectedRevisionId);
                const switching = revisionSwitchingId === revisionId;
                const tone = revisionTabTone(revisionId, index);

                return (
                  <button
                    key={revisionId || revision.id}
                    type="button"
                    role="tab"
                    className="doc-writer-revision-tab"
                    style={s.revisionTab(tone, active, switching)}
                    aria-selected={active}
                    aria-controls={PRINT_TAG_ID}
                    aria-busy={switching}
                    title={`Open ${tabLabel}`}
                    onClick={() => loadRevisionForEditing(revisionId)}
                  >
                    <span style={s.revisionTabInner}>
                      {switching ? (
                        <span style={s.revisionTabLoading} aria-hidden="true" />
                      ) : null}
                      <span>{tabLabel}</span>
                    </span>
                  </button>
                );
              })}
            </div>

            {previewingId ? (
              <div style={s.paneHeader}>
                <span style={{ ...s.previewTag, marginLeft: 0 }}>
                  Viewing the saved version in read-only mode
                </span>
              </div>
            ) : null}

            {/* Revision History — only the selected D/R version remains visible */}
            <div className="doc-writer-editor-section-pad" style={s.sectionPad}>
              <DocRevisionHistory
                items={visibleRevisions}
                currentId={selectedRevisionId}
                locked={session.status === "locked"}
                onView={(id) => {
                  const found = revisions.find((r) => r.id === id);
                  if (found?.html) {
                    if (!previewingId) {
                      prePreviewHtmlRef.current =
                        readLiveCanvasDocument("save").html || localHtml;
                    }
                    setPreviewingId(id);
                    replaceCanvasDocument(normalizeModelOutputToHtml(found.html));
                    addToast("Viewing past revision (read-only).", "info");
                  } else {
                    handleViewRevision(id);
                  }
                }}
                onRestore={(id) => {
                  handleRestoreRevision(id);
                }}
                onSave={async (revId) => {
                  if (session.status === "locked") {
                    addToast("This document is finalized and read-only.", "error");
                    return;
                  }
                  try {
                    setSaving(true);

                    const prepared = readLiveCanvasDocument("save");
                    const exactHtml = String(prepared.html || "");
                    const exactText = String(prepared.text || "");

                    if (!exactHtml.trim()) {
                      throw new Error("The selected revision has no content to save.");
                    }

                    await api.saveExactRevision({
                      sessionId: session.sessionId,
                      revisionId: revId,
                      contentHtml: exactHtml,
                      contentText: exactText,
                      title: session.title,
                      makeCurrent: true,
                    });

                    // Mirror the saved Canvas document into Page state for
                    // dirty/localStorage UI only. Do not rehydrate Canvas from it.
                    setLocalHtml(exactHtml);
                    setSession((prev) => ({ ...prev, contentHtml: exactHtml }));
                    setRevisions((prev) =>
                      prev.map((revision) =>
                        revision.id === revId ? { ...revision, html: exactHtml } : revision
                      )
                    );
                    setLastSavedHtml(exactHtml);
                    setLastSavedTitle(session.title);
                    setDirty(false);
                    addToast("Saved to this revision.", "success");
                  } catch (e) {
                    console.error("[DocWriterEditorPage] ❌ saveExactRevision failed:", e);
                    addToast("The document could not be saved. Please try again.", "error");
                  } finally {
                    setSaving(false);
                  }
                }}
                onRequestEdit={(id) => handleRequestEdit(id)}
                onPreviewPdf={handlePreviewExactRevision}
                onFinalize={handleFinalizeExactRevision}
              />
            </div>

            {/* EDITOR AREA (tagged) */}
            <div
              id={PRINT_TAG_ID}
              data-doc-tag="primary"
              data-doc-role="editor-content"
              data-doc-canvas-mount="stable"
              className="editor-typing-wrap doc-writer-editor-canvas-shell"
              style={{ ...s.canvasMountShell, ...s.editorTypingFix }}
            >
              {canvasEditorNode}
            </div>

            {/* Required revision request controls */}
            <div
              className="doc-writer-modern-fields doc-writer-editor-section-pad"
              style={s.sectionPad}
            >
              <div
                style={{
                  ...s.requestIntro,
                  marginBottom: revisionRequestCollapsed ? 0 : s.requestIntro.marginBottom,
                }}
              >
                <div style={s.requestBadge}>✦</div>
                <div style={s.requestContent}>
                  <h2 style={{ ...s.requestTitle, marginBottom: 0 }}>
                    Request changes to this version
                  </h2>
                </div>
                <button
                  type="button"
                  className="doc-writer-editor-collapse-button"
                  style={s.collapseButton}
                  aria-expanded={!revisionRequestCollapsed}
                  aria-controls="doc-writer-editor-revision-request-fields"
                  aria-label={
                    revisionRequestCollapsed
                      ? "Show revision request options"
                      : "Hide revision request options"
                  }
                  title={
                    revisionRequestCollapsed
                      ? "Show revision request options"
                      : "Hide revision request options"
                  }
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() =>
                    setRevisionRequestCollapsed((current) => !current)
                  }
                >
                  {revisionRequestCollapsed ? "+" : "−"}
                </button>
              </div>

              <div
                id="doc-writer-editor-revision-request-fields"
                style={s.collapsibleContent(revisionRequestCollapsed)}
              >
                <div style={s.divider} />

                <div
                  id="doc-writer-editor-required-preferences"
                aria-invalid={Boolean(
                  revisionValidationErrors.tone ||
                    revisionValidationErrors.readingLevel
                )}
                style={{
                  ...s.validationBlock,
                  ...(revisionValidationErrors.tone ||
                  revisionValidationErrors.readingLevel
                    ? s.validationBlockError
                    : null),
                }}
              >
                <div style={s.labelRow}>
                  <span style={{ ...s.label, marginBottom: 0 }}>
                    Tone & Reading Level
                  </span>
                  <span style={s.requiredPill}>Required</span>
                </div>
                <DocToneSelect
                  value={changeTone}
                  onChange={(value) => {
                    setChangeTone(value);
                    clearRevisionValidationError("tone");
                  }}
                  readingLevel={changeReadingLevel}
                  onReadingLevelChange={(value) => {
                    setChangeReadingLevel(value);
                    clearRevisionValidationError("readingLevel");
                  }}
                />
                {revisionValidationErrors.tone ||
                revisionValidationErrors.readingLevel ? (
                  <div style={s.validationMessage}>
                    {revisionValidationErrors.tone ||
                      revisionValidationErrors.readingLevel}
                  </div>
                ) : null}
              </div>

              <div
                id="doc-writer-editor-required-language"
                aria-invalid={Boolean(revisionValidationErrors.language)}
                style={{
                  ...s.validationBlock,
                  ...(revisionValidationErrors.language
                    ? s.validationBlockError
                    : null),
                }}
              >
                <div style={s.labelRow}>
                  <span style={{ ...s.label, marginBottom: 0 }}>Language</span>
                  <span style={s.requiredPill}>Required</span>
                </div>
                <DocLanguageSelect
                  value={changeLanguage}
                  onChange={(value) => {
                    setChangeLanguage(value);
                    clearRevisionValidationError("language");
                  }}
                  defaultShowExtended={false}
                />
                {revisionValidationErrors.language ? (
                  <div style={s.validationMessage}>
                    {revisionValidationErrors.language}
                  </div>
                ) : null}
              </div>

              <div
                id="doc-writer-editor-required-details"
                aria-invalid={Boolean(
                  revisionValidationErrors.details ||
                    revisionValidationErrors.lengthPreference
                )}
                style={{
                  ...s.validationBlock,
                  marginBottom: 0,
                  ...(revisionValidationErrors.details ||
                  revisionValidationErrors.lengthPreference
                    ? s.validationBlockError
                    : null),
                }}
              >
                <div style={s.labelRow}>
                  <span style={{ ...s.label, marginBottom: 0 }}>
                    Revision Details & Length
                  </span>
                  <span style={s.requiredPill}>Required</span>
                </div>
                <DocDynamicFieldsForm
                  schema={schema}
                  values={fieldValues}
                  onChange={(values) => {
                    setFieldValues(values);
                    if (String(values?.details || "").trim().length >= 10) {
                      clearRevisionValidationError("details");
                    }
                    const lengthValue = values?.lengthPreference;
                    const lengthMode = String(
                      typeof lengthValue === "string"
                        ? lengthValue
                        : lengthValue?.mode || ""
                    ).trim();
                    if (lengthMode) {
                      clearRevisionValidationError("lengthPreference");
                    }
                  }}
                  uid={currentUser?.uid}
                  email={currentUser?.email}
                  userType="individual"
                  sessionId={session.sessionId || "editor-session"}
                  voiceAccessContext="editor"
                />
                {revisionValidationErrors.details ||
                revisionValidationErrors.lengthPreference ? (
                  <div style={s.validationMessage}>
                    {revisionValidationErrors.details ||
                      revisionValidationErrors.lengthPreference}
                  </div>
                ) : null}
              </div>

              <label
                id="doc-writer-editor-required-terms"
                style={s.termsAgreement(Boolean(revisionValidationErrors.terms))}
              >
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(event) => {
                    setTermsAccepted(event.target.checked);
                    if (event.target.checked) {
                      clearRevisionValidationError("terms");
                    }
                  }}
                  disabled={loading || session.status === "locked"}
                  style={s.termsCheckbox}
                  aria-invalid={Boolean(revisionValidationErrors.terms)}
                />
                <span style={s.termsText}>
                  I agree to the{" "}
                  <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">
                    Terms of Use
                  </a>{" "}
                  and{" "}
                  <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>{" "}
                  for this revision request.
                  {revisionValidationErrors.terms ? (
                    <span style={{ ...s.validationMessage, display: "block" }}>
                      {revisionValidationErrors.terms}
                    </span>
                  ) : null}
                </span>
              </label>
              </div>
            </div>

            <div
              className="doc-writer-editor-apply-bar"
              style={{ padding: "0 18px 18px" }}
            >
              <DocGenerateDraftBar
                disabled={session.status === "locked"}
                onGenerate={handleApplyRevision}
                isLoading={loading}
                noteText="Required fields are checked before the revision is submitted."
                buttonLabel="Apply Revision"
                loadingLabel="Applying…"
                isLoggedIn={!!currentUser}
                onLoginRedirect={() =>
                  navigate("/login", {
                    state: { from: location.pathname + location.search },
                  })
                }
              />
            </div>
          </section>
        </main>


      </div>

      {pdfViewerOpen ? <div aria-hidden="true" style={s.pdfPreviewBackdrop} /> : null}

      <FileViewerModal
        open={pdfViewerOpen}
        onClose={closePdfPreviewViewer}
        record={pdfViewerRecord}
      />

      {/* Loading Overlay */}
      {loading && (
        <div style={s.loadingOverlay}>
          <div
            style={{
              background: "#111827",
              color: "#fff",
              padding: "10px 14px",
              borderRadius: 10,
            }}
          >
            Working…
          </div>
        </div>
      )}

      {/* Toasts */}
      <div style={s.toastWrap}>
        {toasts.map((t) => (
          <div key={t.id} style={s.toast(t.type)}>
            {t.message}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DocWriterEditorPage;
